import { useState, useEffect } from "react";
import { AuthProvider, useAuth } from './AuthContext';
import LoginModal from './components/LoginModal';
import RegisterModal from './components/RegisterModal';
import AuthCallback from './components/AuthCallback';

// Mobile Services
import MobileService from './services/MobileService';
import NotificationService from './services/NotificationService';
import OfflineService from './services/OfflineService';
import CalendarService from './services/CalendarService';
import { Capacitor } from '@capacitor/core';

// Mobile Components
import MobileSettings from './components/MobileSettings';
import YogaChecker from './components/YogaChecker';
import "@/App.css";
import "./mobile-app.css";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Switch } from "./components/ui/switch";
import { Textarea } from "./components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Calendar, Clock, Sunrise, Moon, Plus, ChevronLeft, ChevronRight, Grid, List, Eye, CalendarDays, MapPin, Star, Settings, Smartphone, Menu, X, User, LogIn, Bell } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Event Planning Component
const EventPlanner = ({ currentDate, onEventCreated }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [calendarType, setCalendarType] = useState("devimana"); // 'devimana' or 'gregorian'
  const [eventDate, setEventDate] = useState(currentDate?.date || {});
  const [gregorianDate, setGregorianDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedDateTithi, setSelectedDateTithi] = useState(null);

  useEffect(() => {
    if (currentDate?.date) {
      setEventDate(currentDate.date);
    }
  }, [currentDate]);

  // Fetch tithi for selected Gregorian date
  const fetchTithiForDate = async (dateStr) => {
    try {
      const response = await axios.post(`${API}/tithi/calculate`, {
        date: dateStr,
        latitude: 28.6139, // Default to Delhi coordinates
        longitude: 77.2090,
        location_name: "Delhi"
      });
      
      // Transform API response to match frontend expectations
      const tithiData = response.data;
      if (tithiData.current_tithi) {
        setSelectedDateTithi({
          tithi_name: tithiData.current_tithi.tithi_name || tithiData.current_tithi.name,
          paksha: tithiData.current_tithi.paksha,
          tithi_progress: tithiData.current_tithi.progress
        });
      } else {
        setSelectedDateTithi(null);
      }
    } catch (error) {
      console.error('Failed to fetch tithi for date:', error);
      setSelectedDateTithi(null);
    }
  };

  // Handle Gregorian date change
  useEffect(() => {
    if (calendarType === 'gregorian' && gregorianDate) {
      fetchTithiForDate(gregorianDate);
    }
  }, [gregorianDate, calendarType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let eventData;
    
    try {
      if (calendarType === 'devimana') {
        eventData = {
          title,
          description,
          yuga: eventDate.yuga,
          parivritti: eventDate.parivritti,
          varsha: eventDate.varsha,
          masa: eventDate.masa,
          divasa: eventDate.divasa,
          ghatika: eventDate.ghatika || 1,
          calendar_type: 'devimana'
        };
      } else {
        eventData = {
          title,
          description,
          gregorian_date: gregorianDate,
          calendar_type: 'gregorian',
          tithi_info: selectedDateTithi
        };
      }

      console.log('Creating event with data:', eventData);
      
      // Debug authentication state
      const token = localStorage.getItem('auth_token');
      console.log('🔑 Token from localStorage:', token ? `${token.substring(0, 20)}...` : 'No token found');
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      // Ensure authentication token is included
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      console.log('📡 Making request with headers:', headers);
      const response = await axios.post(`${API}/devimana/events`, eventData, { headers });
      
      if (response.data) {
        console.log('✅ Event created successfully:', response.data);
        
        // Mobile integrations (only if on mobile device)
        if (MobileService.isMobile()) {
          const integrationResults = {};
          
          try {
            // 1. Add event to device calendar
            console.log('📅 Adding event to device calendar...');
            const calendarResult = await CalendarService.addEventToCalendar(response.data);
            integrationResults.calendar = calendarResult;
            
            if (calendarResult.success) {
              console.log('✅ Event added to calendar:', calendarResult.calendarName);
              toast.success(`Event added to ${calendarResult.calendarName || 'device calendar'}!`);
            } else {
              console.warn('⚠️ Calendar sync failed:', calendarResult.error);
              toast.warning('Event created but calendar sync failed');
            }
            
            // 2. Schedule 24-hour notification reminder
            console.log('📱 Scheduling 24-hour notification...');
            const notificationResult = await NotificationService.scheduleEventReminder(response.data);
            integrationResults.notification = notificationResult;
            
            if (notificationResult.success) {
              const reminderDate = new Date(notificationResult.reminderTime);
              console.log('✅ 24-hour reminder scheduled for:', reminderDate.toLocaleString());
              toast.success(`Reminder set for ${reminderDate.toLocaleDateString()} at ${reminderDate.toLocaleTimeString()}`);
            } else {
              console.warn('⚠️ Notification scheduling failed:', notificationResult.error);
              if (notificationResult.error.includes('too soon')) {
                toast.info('Event is tomorrow - no 24-hour reminder needed');
              } else {
                toast.warning('Event created but reminder failed');
              }
            }
            
            // Log integration summary
            console.log('🔗 Mobile integrations completed:', integrationResults);
            
          } catch (mobileError) {
            console.error('📱 Mobile integration error:', mobileError);
            toast.warning('Event created but some mobile features failed');
          }
        }
        
        // Cache event offline
        try {
          await OfflineService.cacheEvents([response.data]);
          console.log('💾 Event cached offline successfully');
        } catch (cacheError) {
          console.warn('💾 Failed to cache event offline:', cacheError);
        }
        
        toast.success('Event created successfully!');
        
        // Refresh events list
        if (onEventCreated) {
          onEventCreated();
        }
      }
      
      setTitle("");
      setDescription("");
      setIsOpen(false);
      if (onEventCreated) onEventCreated();
    } catch (error) {
      console.error('Error creating event:', error);
      console.error('Event data sent:', eventData);
      console.error('Response:', error.response?.data);
      
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Failed to create event';
      toast.error(`Failed to create event: ${errorMessage}`);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-xs sm:text-sm px-2 sm:px-4 py-1 sm:py-2" data-testid="create-event-button">
          <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Plan Divine Event</span>
          <span className="sm:hidden">Plan Event</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle>Plan Event in Divine Timeline</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Event Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter event title"
              required
              data-testid="event-title-input"
            />
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Event details"
              rows={3}
              data-testid="event-description-input"
            />
          </div>
          
          {/* Calendar Type Selection */}
          <div>
            <Label>Calendar Type</Label>
            <div className="flex space-x-4 mt-2">
              <label className="flex items-center space-x-2 cursor-pointer" htmlFor="devimana-calendar">
                <input
                  id="devimana-calendar"
                  type="radio"
                  name="calendarType"
                  value="devimana"
                  checked={calendarType === 'devimana'}
                  onChange={(e) => setCalendarType(e.target.value)}
                  className="text-emerald-600 cursor-pointer"
                />
                <span className="text-sm">Devimana Calendar</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer" htmlFor="gregorian-calendar">
                <input
                  id="gregorian-calendar"
                  type="radio"
                  name="calendarType"
                  value="gregorian"
                  checked={calendarType === 'gregorian'}
                  onChange={(e) => setCalendarType(e.target.value)}
                  className="text-emerald-600 cursor-pointer"
                />
                <span className="text-sm">Gregorian Calendar</span>
              </label>
            </div>
          </div>

          {/* Conditional Date Display */}
          {calendarType === 'gregorian' ? (
            <div>
              <Label htmlFor="gregorianDate">Select Date</Label>
              <Input
                id="gregorianDate"
                type="date"
                value={gregorianDate}
                onChange={(e) => setGregorianDate(e.target.value)}
                className="mt-1"
                data-testid="gregorian-date-input"
              />
              {selectedDateTithi && (
                <div className="mt-2 p-3 bg-blue-50 rounded text-sm">
                  <div><strong>Tithi:</strong> {selectedDateTithi.tithi_name}</div>
                  <div><strong>Paksha:</strong> {selectedDateTithi.paksha}</div>
                  <div><strong>Progress:</strong> {selectedDateTithi.tithi_progress ? (selectedDateTithi.tithi_progress * 100).toFixed(1) : '0'}%</div>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 text-sm p-3 bg-amber-50 rounded">
              <div><strong>युग:</strong> {eventDate.yuga} - {eventDate.yuga_name}</div>
              <div><strong>परिवृत्ति:</strong> {eventDate.parivritti} - {eventDate.parivritti_name}</div>
              <div><strong>वर्ष:</strong> {eventDate.varsha} - {eventDate.varsha_name}</div>
              <div><strong>मास:</strong> {eventDate.masa} - {eventDate.masa_name}</div>
              <div><strong>दिवस:</strong> {eventDate.divasa} - {eventDate.divasa_name}</div>
              <div><strong>वासर:</strong> {eventDate.vasara_name}</div>
            </div>
          )}
          
          <Button type="submit" className="w-full" data-testid="create-event-submit">
            {calendarType === 'devimana' ? 'Create Event in Divine Timeline' : 'Create Event'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const DevimanaCalendar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const [currentDate, setCurrentDate] = useState(null);
  
  // Mobile-specific states
  const [isMobile, setIsMobile] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ghatika, setGhatika] = useState(null);
  const [events, setEvents] = useState([]);
  const [activeView, setActiveView] = useState("dashboard");
  
  // Navigation state for different calendar views
  const [viewingMasa, setViewingMasa] = useState(null);
  const [viewingVarsha, setViewingVarsha] = useState(null);
  const [gregorianDate, setGregorianDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null); // For dashboard drill-down
  const [selectedDevimanaDate, setSelectedDevimanaDate] = useState(null);
  
  // Tithi calculation state
  const [tithiData, setTithiData] = useState(null);
  const [traditionalTithiData, setTraditionalTithiData] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Start from Sunday
    return startOfWeek;
  });
  const [weeklyTithis, setWeeklyTithis] = useState([]);
  const [selectedCity, setSelectedCity] = useState(() => 
    localStorage.getItem('devimana_selected_city') || "Delhi"
  );
  const [selectedCityData, setSelectedCityData] = useState(() => {
    const saved = localStorage.getItem('devimana_selected_city_data');
    return saved ? JSON.parse(saved) : null;
  });
  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  const [locationSearchResults, setLocationSearchResults] = useState([]);
  const [showLocationResults, setShowLocationResults] = useState(false);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [customLocation, setCustomLocation] = useState({ lat: null, lon: null, name: "" });
  const [majorCities, setMajorCities] = useState({});
  const [sunriseSunset, setSunriseSunset] = useState(null);
  const [dinaNitya, setDinaNitya] = useState(null);
  const [calculationMethod, setCalculationMethod] = useState("surya_siddhanta"); // Toggle between traditional methods
  const [useAuthenticSiddhanta, setUseAuthenticSiddhanta] = useState(false);
  
  // Settings state
  const [showSettings, setShowSettings] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  
  // PWA Installation state
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [language, setLanguage] = useState(() => 
    localStorage.getItem('devimana_language') || 'english'
  );
  const [tithiMethod, setTithiMethod] = useState(() => 
    localStorage.getItem('devimana_tithi_method') || 'visuddha_siddhanta'
  );
  const [implementationType, setImplementationType] = useState(() => 
    localStorage.getItem('devimana_implementation') || 'traditional'
  );
  const [kulaType, setKulaType] = useState(() => 
    localStorage.getItem('devimana_kula_type') || 'shri_kula'
  );
  const [tantraType, setTantraType] = useState(() => 
    localStorage.getItem('devimana_tantra_type') || 'saubhagya'
  );

  // Date lookup state
  const [lookupDate, setLookupDate] = useState(new Date().toISOString().split('T')[0]);
  const [lookupCredentials, setLookupCredentials] = useState(null);
  const [lookupMethod, setLookupMethod] = useState('visuddha_siddhanta');
  const [lookupDailyTithis, setLookupDailyTithis] = useState(null);
  
  // Location search states for date lookup
  const [lookupLocationQuery, setLookupLocationQuery] = useState('Delhi');
  const [lookupLocationResults, setLookupLocationResults] = useState([]);
  const [lookupLocationData, setLookupLocationData] = useState({ name: 'Delhi', lat: 28.6139, lon: 77.2090, timezone: 'Asia/Kolkata' });
  
  // Current daily tithis state
  const [currentDailyTithis, setCurrentDailyTithis] = useState(null);
  const [currentLocationTimezone, setCurrentLocationTimezone] = useState('Asia/Kolkata');
  
  // Sankalpa modal state
  const [showSankalpa, setShowSankalpa] = useState(false);
  const [showDateSankalpa, setShowDateSankalpa] = useState(false);

  // Yoga checker state
  const [showYogaChecker, setShowYogaChecker] = useState(false);

  // Authentication modal states
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  // Sankalpa states
  const [currentSankalpa, setCurrentSankalpa] = useState('');
  const [dateSankalpa, setDateSankalpa] = useState('');
  const [sankalpaPending, setSankalpaPending] = useState(false);
  
  // Mobile settings state
  const [showMobileSettings, setShowMobileSettings] = useState(false);
  
  const { generateSankalpa: generateSankalpaAPI } = useAuth();

  // Language translations (moved to top to avoid reference errors)
  const translations = {
    english: {
      // Settings
      settings: "Settings",
      tithiPreferences: "Tithi Calculation Preferences", 
      calculationMethod: "Calculation Method",
      implementation: "Implementation Type",
      kulaPreferences: "Kula Tradition",
      shrikula: "Shrikula",
      kalikula: "Kalikula",
      languagePreferences: "Language Preferences",
      selectLanguage: "Select Language",
      saveSettings: "Save Settings",
      settingsSaved: "Settings saved successfully!",
      generateSankalpa: "Generate Saṅkalpa",
      sankalpaTitle: "Saṅkalpa Declaration",
      suryaSiddhanta: "Surya Siddhanta",
      vishuddhaSiddhanta: "Visuddha Siddhanta",
      traditional: "Traditional",
      authentic: "Authentic Siddhanta",
      modern: "Modern Approximation",
      english: "English",
      bengali: "Bengali", 
      sanskrit: "Sanskrit",
      generalSettings: "General Preferences",
      aboutMethod: "About Calculation Methods",
      suryaDescription: "Ancient astronomical text, traditional Hindu calendar calculations",
      vishddhaDescription: "Refined version with corrected astronomical constants (1890s)",
      traditionalDesc: "Simplified calculations for general use",
      authenticDesc: "Historically accurate Siddhanta mathematical models",
      
      // Dashboard & Main Interface
      devimanaCalendar: "देवीमान अष्टांग Calendar",
      completeDevimanaCalendar: "Complete देवीमान अष्टांग Calendar",
      currentDate: "Complete Current Date",
      todaysVasara: "Today's वासर (Vasara)",
      currentGhatika: "Current घटिका (Ghatika)",
      ghatikaOf60: "घटिका {current} of 60",
      minuteInGhatika: "Minute {current} of 24 in current घटिका",
      ghatikaProgress: "{progress}% Complete",
      realTimeProgress: "Real-time घटिका Progress",
      sunriseSunset: "Sunrise & Sunset",
      sunrise: "Sunrise", 
      sunset: "Sunset",
      location: "Location",
      tattwaSystem: "Tattwa System", 
      currentTattwas: "Current Tattwas",
      yugaTattwa: "Yuga Tattwa:",
      varshaTattwa: "Varsha Tattwa:",
      divasaTattwa: "Divasa Tattwa:",
      currentMasa: "Current मास",
      masaOf16: "मास {current} of 16",
      divasaOf36: "दिवस {current} of 36 in current मास",
      masaProgress: "मास Progress",
      today: "Today",
      
      // Tab Names
      dashboard: "Dashboard",
      weekViews: "Week Views", 
      yearViews: "Year Views",
      traditionalTithi: "Traditional Tithi",
      events: "Events",
      dateLookup: "Date Lookup",
      settings: "Settings",
      
      // Action Buttons
      planDivineEvent: "Plan Divine Event",
      weekViewsLabel: "Week Views (9+7 Day)",
      yearViewsLabel: "Year Views (16+12)",
      
      // Vasaras (9-day cycle) - Using Sanskrit names
      vasara1: "अं प्रह्लादानन्दनाथ",
      vasara2: "लृं सकलानन्दनाथ", 
      vasara3: "कं कुमारानन्दनाथ",
      vasara4: "चं वशिष्ठानन्दनाथ",
      vasara5: "टं क्रोधानन्दनाथ",
      vasara6: "तं सुरानन्दनाथ",
      vasara7: "पं ध्यानानन्दनाथ",
      vasara8: "यं बोधानन्दनाथ",
      vasara9: "षं शुकानन्दनाथ"
    },
    bengali: {
      // Settings
      settings: "সেটিংস",
      tithiPreferences: "তিথি গণনার পছন্দ",
      calculationMethod: "গণনা পদ্ধতি", 
      implementation: "বাস্তবায়ন প্রকার",
      kulaPreferences: "কুল ঐতিহ্য",
      shrikula: "শ্রীকুল",
      kalikula: "কালীকুল",
      languagePreferences: "ভাষার পছন্দ",
      selectLanguage: "ভাষা নির্বাচন করুন",
      saveSettings: "সেটিংস সংরক্ষণ করুন",
      settingsSaved: "সেটিংস সফলভাবে সংরক্ষিত হয়েছে!",
      generateSankalpa: "সংকল্প উৎপন্ন করুন",
      sankalpaTitle: "সংকল্প ঘোষণা",
      suryaSiddhanta: "সূর্য সিদ্ধান্ত",
      vishuddhaSiddhanta: "বিশুদ্ধ সিদ্ধান্ত",
      traditional: "ঐতিহ্যগত",
      authentic: "প্রামাণিক সিদ্ধান্ত",
      modern: "আধুনিক আনুমানিক",
      english: "ইংরেজি",
      bengali: "বাংলা",
      sanskrit: "সংস্কৃত",
      generalSettings: "সাধারণ পছন্দ",
      aboutMethod: "গণনা পদ্ধতি সম্পর্কে",
      suryaDescription: "প্রাচীন জ্যোতির্বিদ্যা গ্রন্থ, ঐতিহ্যবাহী হিন্দু পঞ্জিকা গণনা",
      vishddhaDescription: "সংশোধিত জ্যোতির্বিদ্যা ধ্রুবক সহ পরিশীলিত সংস্করণ (১৮৯০ এর দশক)",
      traditionalDesc: "সাধারণ ব্যবহারের জন্য সরলীকৃত গণনা",
      authenticDesc: "ঐতিহাসিকভাবে নির্ভুল সিদ্ধান্ত গাণিতিক মডেল",
      
      // Dashboard & Main Interface
      devimanaCalendar: "দেবীমান অষ্টাঙ্গ পঞ্চাঙ্গ",
      completeDevimanaCalendar: "সম্পূর্ণ দেবীমান অষ্টাঙ্গ পঞ্চাঙ্গ",
      currentDate: "সম্পূর্ণ বর্তমান তারিখ",
      todaysVasara: "আজকের বাসর",
      currentGhatika: "বর্তমান ঘটিকা",
      ghatikaOf60: "ঘটিকা {current} এর ৬০",
      minuteInGhatika: "বর্তমান ঘটিকায় ২৪ এর মধ্যে {current} মিনিট",
      ghatikaProgress: "{progress}% সম্পূর্ণ",
      realTimeProgress: "রিয়েল-টাইম ঘটিকা অগ্রগতি",
      sunriseSunset: "সূর্যোদয় ও সূর্যাস্ত",
      sunrise: "সূর্যোদয়", 
      sunset: "সূর্যাস্ত",
      location: "অবস্থান",
      tattwaSystem: "তত্ত্ব সিস্টেম",
      currentTattwas: "বর্তমান তত্ত্বসমূহ",
      yugaTattwa: "যুগ তত্ত্ব:",
      varshaTattwa: "বর্ষ তত্ত্ব:",
      divasaTattwa: "দিবস তত্ত্ব:",
      currentMasa: "বর্তমান মাস",
      masaOf16: "মাস {current} এর ১৬",
      divasaOf36: "বর্তমান মাসে ৩৬ এর মধ্যে {current} দিবস",
      masaProgress: "মাসের অগ্রগতি",
      today: "আজ",
      
      // Tab Names  
      dashboard: "ড্যাশবোর্ড",
      weekViews: "সপ্তাহের দৃশ্য",
      yearViews: "বছরের দৃশ্য",
      traditionalTithi: "ঐতিহ্যবাহী তিথি",
      events: "ঘটনাসমূহ",
      dateLookup: "তারিখ অনুসন্ধান",
      settings: "সেটিংস",
      
      // Action Buttons
      planDivineEvent: "দৈব ঘটনা পরিকল্পনা করুন",
      weekViewsLabel: "সপ্তাহের দৃশ্য (৯+৭ দিন)",
      yearViewsLabel: "বছরের দৃশ্য (১৬+১২)",
      
      // Vasaras (9-day cycle) - keeping Sanskrit names but with Bengali labels
      vasara1: "প্রহ্লাদানন্দনাথ",
      vasara2: "সকলানন্দনাথ",
      vasara3: "কুমারানন্দনাথ", 
      vasara4: "বশিষ্ঠানন্দনাথ",
      vasara5: "ক্রোধানন্দনাথ",
      vasara6: "সুরানন্দনাথ",
      vasara7: "ধ্যানানন্দনাথ",
      vasara8: "বোধানন্দনাথ",
      vasara9: "শুকানন্দনাথ"
    },
    sanskrit: {
      // Settings
      settings: "सेटिंग्स",
      tithiPreferences: "तिथि गणना प्राथमिकताएं",
      calculationMethod: "गणना पद्धति",
      implementation: "कार्यान्वयन प्रकार",
      kulaPreferences: "कुल परम्परा",
      shrikula: "श्रीकुल",
      kalikula: "कालीकुल",
      languagePreferences: "भाषा प्राथमिकताएं",
      selectLanguage: "भाषा चुनें",
      saveSettings: "सेटिंग्स सहेजें",
      settingsSaved: "सेटिंग्स सफलतापूर्वक सहेजी गई!",
      generateSankalpa: "संकल्प उत्पन्न करें",
      sankalpaTitle: "संकल्प घोषणा",
      suryaSiddhanta: "सूर्य सिद्धान्त",
      vishuddhaSiddhanta: "विशुद्ध सिद्धान्त", 
      traditional: "पारम्परिक",
      authentic: "प्रामाणिक सिद्धान्त",
      modern: "आधुनिक अनुमान",
      english: "अंग्रेजी",
      bengali: "बंगाली",
      sanskrit: "संस्कृत",
      generalSettings: "सामान्य प्राथमिकताएं",
      aboutMethod: "गणना पद्धति के बारे में",
      suryaDescription: "प्राचीन ज्योतिष ग्रन्थ, पारम्परिक हिन्दू पञ्चाङ्ग गणना",
      vishddhaDescription: "संशोधित खगोलीय नियतांक के साथ परिष्कृत संस्करण (१८९० दशक)",
      traditionalDesc: "सामान्य उपयोग हेतु सरलीकृत गणना",
      authenticDesc: "ऐतिहासिक रूप से सटीक सिद्धान्त गणितीय मॉडल",
      
      // Dashboard & Main Interface
      devimanaCalendar: "देवीमान अष्टांग पञ्चांग",
      completeDevimanaCalendar: "सम्पूर्ण देवीमान अष्टांग पञ्चांग",
      currentDate: "आज का दिनांक (Complete Current Date)",
      todaysVasara: "आज का वासर (Today's Vasara)",
      currentGhatika: "वर्तमान घटिका",
      ghatikaOf60: "घटिका {current} of 60",
      minuteInGhatika: "वर्तमान घटिका में 24 का {current} मिनट",
      ghatikaProgress: "{progress}% पूर्ण",
      realTimeProgress: "रियल-टाइम घटिका प्रगति",
      sunriseSunset: "सूर्योदय एवं सूर्यास्त",
      sunrise: "सूर्योदय", 
      sunset: "सूर्यास्त",
      location: "स्थान",
      tattwaSystem: "तत्त्व सिस्टम",
      currentTattwas: "वर्तमान तत्त्व",
      yugaTattwa: "युग तत्त्व:",
      varshaTattwa: "वर्ष तत्त्व:",
      divasaTattwa: "दिवस तत्त्व:",
      currentMasa: "वर्तमान मास",
      masaOf16: "मास {current} of 16",
      divasaOf36: "वर्तमान मास में 36 का {current} दिवस",
      masaProgress: "मास प्रगति",
      today: "आज",
      
      // Tab Names
      dashboard: "डैशबोर्ड",
      weekViews: "सप्ताह दृश्य",
      yearViews: "वर्ष दृश्य", 
      traditionalTithi: "पारम्परिक तिथि",
      events: "कार्यक्रम",
      dateLookup: "दिनांक खोज",
      settings: "सेटिंग्स",
      
      // Action Buttons
      planDivineEvent: "दिव्य कार्यक्रम योजना",
      weekViewsLabel: "सप्ताह दृश्य (9+7 दिन)",
      yearViewsLabel: "वर्ष दृश्य (16+12)",
      
      // Vasaras (9-day cycle)
      vasara1: "प्रह्लादानन्दनाथ",
      vasara2: "सकलानन्दनाथ",
      vasara3: "कुमारानन्दनाथ",
      vasara4: "वशिष्ठानन्दनाथ", 
      vasara5: "क्रोधानन्दनाथ",
      vasara6: "सुरानन्दनाथ",
      vasara7: "ध्यानानन्दनाथ",
      vasara8: "बोधानन्दनाथ",
      vasara9: "शुकानन्दनाथ"
    }
  };

  const t = translations[language] || translations.sanskrit;
  const [showTraditional, setShowTraditional] = useState(true); // Toggle between modern and traditional

  const fetchCurrentDate = async (forceKulaType = null) => {
    try {
      // Use provided kula type or current state (already in correct format)
      const currentKula = forceKulaType || kulaType;
      const kulaTypeForAPI = currentKula || 'shri_kula'; // Default to shri_kula
      
      console.log(`🔄 fetchCurrentDate called with kulaType: ${currentKula} -> ${kulaTypeForAPI}`);
      
      const response = await axios.get(`${API}/devimana/current?kula_type=${kulaTypeForAPI}`);
      setCurrentDate(response.data);
      
      console.log(`✅ Current date updated with ${kulaTypeForAPI} names:`, {
        masa_name: response.data?.date?.masa_name,
        vasara_name: response.data?.date?.vasara_name
      });
      
      // Set initial Ghatika from backend
      setGhatika(response.data.time);
    } catch (error) {
      console.error('Error fetching current date:', error);
      toast.error('Failed to load Devimana calendar data');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    if (!isAuthenticated) {
      console.log('User not authenticated, skipping event fetch');
      setEvents([]);
      return;
    }

    try {
      console.log('Fetching events for authenticated user');
      const response = await axios.get(`${API}/devimana/events`);
      console.log('Events loaded successfully:', response.data.length);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to load events:', error);
      console.error('Auth status:', isAuthenticated, 'User:', user?.username);
      if (error.response?.status === 401) {
        console.error('Authentication failed - user may need to login again');
      }
      setEvents([]);
    }
  };

  const deleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/devimana/events/${eventId}`);
      toast.success("Event deleted successfully");
      fetchEvents();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  // Tithi-related functions
  const fetchMajorCities = async () => {
    try {
      const response = await axios.get(`${API}/tithi/cities`);
      setMajorCities(response.data.cities);
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    }
  };

  const fetchTithiData = async (cityName = selectedCity, customLat = null, customLon = null) => {
    try {
      let response;
      if (customLat && customLon) {
        // Use custom coordinates
        response = await axios.post(`${API}/tithi/calculate`, {
          date: new Date().toISOString(),
          latitude: customLat,
          longitude: customLon,
          location_name: customLocation.name || "Custom Location"
        });
      } else {
        // Use city name
        response = await axios.get(`${API}/tithi/current/${cityName}`);
      }
      
      setTithiData(response.data);
      if (response.data.error) {
        toast.error(`Failed to calculate tithi: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Failed to fetch tithi data:', error);
      toast.error('Failed to fetch tithi information');
    }
  };

  const fetchTithiForSelectedDate = async (dateStr, lat, lon, locationName) => {
    try {
      const response = await axios.post(`${API}/tithi/comprehensive`, {
        date: dateStr,
        latitude: lat,
        longitude: lon,
        location_name: locationName
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch tithi for selected date:', error);
      return null;
    }
  };

  const fetchTraditionalTithi = async (cityName = selectedCity, method = tithiMethod, forceKulaType = null) => {
    try {
      // Use provided kula type or current state (already in correct format)
      const currentKula = forceKulaType || kulaType;
      const kulaTypeForAPI = currentKula || 'shri_kula'; // Default to shri_kula
      
      console.log(`🔄 fetchTraditionalTithi called with kulaType: ${currentKula} -> ${kulaTypeForAPI}`);
      
      const endpoint = implementationType === 'authentic'
        ? `${API}/tithi/authentic/${method}/current/${cityName}?kula_type=${kulaTypeForAPI}`
        : `${API}/tithi/traditional/${method}/current/${cityName}?kula_type=${kulaTypeForAPI}`;
        
      const response = await axios.get(endpoint);
      setTraditionalTithiData(response.data);
      
      console.log(`✅ Traditional tithi updated with ${kulaTypeForAPI}:`, response.data.tithi_name);
      
      if (response.data.error) {
        toast.error(`Failed to calculate traditional tithi: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Failed to fetch traditional tithi:', error);
      toast.error('Failed to fetch traditional tithi information');
    }
  };

  const fetchWeeklyTithis = async (weekStart = currentWeekStart, method = tithiMethod, forceKulaType = null) => {
    try {
      // Use provided kula type or current state (already in correct format)
      const currentKula = forceKulaType || kulaType;
      const kulaTypeForAPI = currentKula || 'shri_kula'; // Default to shri_kula
      
      const startDate = weekStart.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      
      console.log(`🔄 fetchWeeklyTithis called with kulaType: ${currentKula} -> ${kulaTypeForAPI}`);
      
      // Use authentic or traditional endpoint based on settings
      const endpoint = implementationType === 'authentic'
        ? `${API}/tithi/authentic/weekly/${method}/${startDate}/${selectedCity}?kula_type=${kulaTypeForAPI}`
        : `${API}/tithi/weekly/${method}/${startDate}/${selectedCity}?kula_type=${kulaTypeForAPI}`;
        
      const response = await axios.get(endpoint);
      
      if (response.data.error) {
        toast.error(`Failed to fetch weekly tithis: ${response.data.error}`);
        setWeeklyTithis([]);
      } else {
        setWeeklyTithis(response.data.week_data || []);
      }
    } catch (error) {
      console.error('Failed to fetch weekly tithis:', error);
      toast.error('Failed to fetch weekly tithi information');
      setWeeklyTithis([]);
    }
  };

  const fetchMonthlyTithis = async (year, month, cityName = selectedCity, method = calculationMethod, forceKulaType = null) => {
    try {
      // Use provided kula type or current state (already in correct format)
      const currentKula = forceKulaType || kulaType;
      const kulaTypeForAPI = currentKula || 'shri_kula'; // Default to shri_kula
      
      console.log(`🔄 fetchMonthlyTithis called with kulaType: ${currentKula} -> ${kulaTypeForAPI}`);
      
      const response = await axios.get(`${API}/tithi/monthly/${method}/${year}/${month}/${cityName}?kula_type=${kulaTypeForAPI}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch monthly tithis:', error);
      return null;
    }
  };

  const compareTraditionalMethods = async (cityName = selectedCity) => {
    try {
      const coordinates = majorCities[cityName];
      if (!coordinates) return null;

      const response = await axios.post(`${API}/tithi/compare`, {
        date: new Date().toISOString(),
        latitude: coordinates.lat,
        longitude: coordinates.lon,
        location_name: cityName
      });
      return response.data;
    } catch (error) {
      console.error('Failed to compare methods:', error);
      return null;
    }
  };

  const searchLocations = async (query) => {
    if (!query || query.length < 2) {
      setLocationSearchResults([]);
      setShowLocationResults(false);
      return;
    }
    
    setIsSearchingLocation(true);
    try {
      const response = await axios.get(`${API}/cities/search?q=${encodeURIComponent(query)}&limit=8`);
      if (response.data.success) {
        setLocationSearchResults(response.data.cities);
        setShowLocationResults(true);
      }
    } catch (error) {
      console.error('Failed to search locations:', error);
      setLocationSearchResults([]);
    } finally {
      setIsSearchingLocation(false);
    }
  };

  const selectLocation = async (city) => {
    setSelectedCity(city.name);
    setSelectedCityData(city);
    setLocationSearchQuery(city.name);
    setShowLocationResults(false);
    
    // Save to localStorage for persistence
    localStorage.setItem('devimana_selected_city', city.name);
    localStorage.setItem('devimana_selected_city_data', JSON.stringify(city));
    
    // Update sunrise/sunset for selected location
    fetchSunriseSunsetFromCoords(city.lat, city.lon, city.name);
    
    // Update tithi calculations for the new location
    fetchTithiFromCoords(city.lat, city.lon, city.name);
    
    // Update daily tithis (Today's Tithis section) for the new location
    fetchCurrentDailyTithis(city.name, tithiMethod, kulaType);
    
    toast.success(`Location set to ${city.name}`);
  };

  const fetchTithiFromCoords = async (lat, lon, cityName) => {
    try {
      const response = await axios.post(`${API}/tithi/calculate`, {
        date: new Date().toISOString().split('T')[0],
        latitude: lat,
        longitude: lon,
        location_name: cityName
      });
      // Handle tithi response if needed
    } catch (error) {
      console.error('Failed to fetch tithi for coordinates:', error);
    }
  };

  const fetchSunriseSunsetFromCoords = async (lat, lon, cityName) => {
    try {
      const today = new Date();
      const response = await fetch(
        `https://api.sunrise-sunset.org/json?lat=${lat}&lng=${lon}&date=${today.toISOString().split('T')[0]}&formatted=0`
      );
      const data = await response.json();
      
      if (data.status === 'OK') {
        setSunriseSunset({
          sunrise: new Date(data.results.sunrise).toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          }),
          sunset: new Date(data.results.sunset).toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          }),
          location: cityName
        });
      }
    } catch (error) {
      console.error('Failed to fetch sunrise/sunset for coordinates:', error);
    }
  };

  const fetchDinaNitya = async (selectedTantraType, forceKulaType = null) => {
    const typeToUse = selectedTantraType || tantraType;
    // Use provided kula type or current state (already in correct format)
    const currentKula = forceKulaType || kulaType;
    const kulaTypeForAPI = currentKula || 'shri_kula'; // Default to shri_kula
    
    console.log(`🔄 fetchDinaNitya called with kulaType: ${currentKula} -> ${kulaTypeForAPI}`);
    
    try {
      const response = await axios.get(`${API}/devimana/dina-nitya?tantra_type=${typeToUse}&location=${selectedCity}&kula_type=${kulaTypeForAPI}`);
      if (response.data.success) {
        setDinaNitya(response.data.dina_nitya);
        console.log(`✅ Dina Nitya updated with ${kulaTypeForAPI}:`, response.data.dina_nitya?.letter);
      }
    } catch (error) {
      console.error('Failed to fetch Dina Nitya:', error);
      setDinaNitya(null);
    }
  };

  const fetchDateCredentials = async (dateStr) => {
    try {
      // Use the selected location data from the city search
      const locationData = lookupLocationData || { name: 'Delhi', lat: 28.6139, lon: 77.2090, timezone: 'Asia/Kolkata' };
      
      // Use kulaType as is (already in correct format)
      const kulaTypeForAPI = kulaType || 'shri_kula'; // Default to shri_kula
      
      const response = await axios.post(`${API}/devimana/date-lookup`, {
        date: dateStr,
        tantra_type: tantraType,  // Pass the user's selected tantra type
        kula_type: kulaTypeForAPI,  // Pass the user's selected kula type
        latitude: locationData.lat,
        longitude: locationData.lon,
        location_name: locationData.name,
        method: lookupMethod
      });
      if (response.data.success) {
        setLookupCredentials(response.data.credentials);
        setLookupDailyTithis(response.data.daily_tithis);  // Store the daily tithis data
      } else {
        setLookupCredentials(null);
        setLookupDailyTithis(null);
      }
    } catch (error) {
      console.error('Failed to fetch date credentials:', error);
      setLookupCredentials(null);
      setLookupDailyTithis(null);
    }
  };

  const generateCurrentSankalpa = async () => {
    setSankalpaPending(true);
    
    if (isAuthenticated) {
      // Use backend API for authenticated users
      try {
        const result = await generateSankalpaAPI();
        if (result.success) {
          setCurrentSankalpa(result.data.sankalpa);
          setSankalpaPending(false);
          return result.data.sankalpa;
        } else {
          console.error('Backend Sankalpa generation failed:', result.error);
          // Fall back to frontend generation
        }
      } catch (error) {
        console.error('Failed to generate Sankalpa via API:', error);
        // Fall back to frontend generation
      }
    }

    // Frontend fallback generation (for non-authenticated users or API failure)
    const currentTithi = traditionalTithiData || {};
    const tithiName = currentTithi.tithi_name || 'प्रतिपदा';
    const paksha = currentTithi.paksha || 'शुक्ल पक्ष';
    
    // Get Dina Nitya letter
    const dinaNityaLetter = dinaNitya?.letter || 'अ';
    
    // Generate the Sankalpa text with Sanskrit names instead of numbers
    const sankalpaText = `श्री आदिगुरोः परशिवस्याज्ञया प्रवर्तमानेन देवीमानेन षट्त्रिंशत्तत्त्वात्मक-सकलप्रपञ्च-सृष्टि-स्थिति-संहार-तिरोधान-अनुग्रहकारिण्याः श्रीपराशक्तेः ऊर्ध्वभ्रूविभ्रमे ${date?.yuga_name || 'अं ज्ञानी'} तत्त्वयुगे ${date?.parivritti_name || 'अं ज्ञानकारी'} तत्त्वपरिवृत्तौ ${date?.varsha_name || 'अं शिवी'}तत्त्ववर्षे ${date?.masa_name || 'अं कला'} नित्यामासे ${date?.divasa_name || 'अं शिव'} तत्त्वदिवसे ${date?.vasara_name || 'अं प्रह्लादानन्दनाथ'} वासरे ${dinaNityaLetter} दिननित्यायां ${paksha} पक्षे ${tithiName} तिथि नित्यायां .... गोत्रोद्भवः/ गोत्रोद्भवा ..... देवशर्मा/ देवी/ दासः/ दासी ........`;
    
    setCurrentSankalpa(sankalpaText);
    setSankalpaPending(false);
    return sankalpaText;
  };

  const generateSankalpaForDate = async (dateStr, credentials) => {
    if (!credentials) return '';
    
    setSankalpaPending(true);
    
    if (isAuthenticated) {
      // Use backend API for authenticated users
      try {
        const result = await generateSankalpaAPI(dateStr);
        if (result.success) {
          const generatedText = result.data.sankalpa;
          setDateSankalpa(generatedText);
          setSankalpaPending(false);
          return generatedText;
        } else {
          console.error('Backend Date Sankalpa generation failed:', result.error);
          // Fall back to frontend generation
        }
      } catch (error) {
        console.error('Failed to generate Date Sankalpa via API:', error);
        // Fall back to frontend generation
      }
    }

    // Frontend fallback generation (for non-authenticated users or API failure)
    const sankalpaText = `श्री आदिगुरोः परशिवस्याज्ञया प्रवर्तमानेन देवीमानेन षट्त्रिंशत्तत्त्वात्मक-सकलप्रपञ्च-सृष्टि-स्थिति-संहार-तिरोधान-अनुग्रहकारिण्याः श्रीपराशक्तेः ऊर्ध्वभ्रूविभ्रमे ${credentials.yuga_name || 'अं ज्ञानी'} तत्त्वयुगे ${credentials.parivritti_name || 'अं ज्ञानकारी'} तत्त्वपरिवृत्तौ ${credentials.varsha_name || 'अं शिवी'}तत्त्ववर्षे ${credentials.masa_name || 'अं कला'} नित्यामासे ${credentials.divasa_name || 'अं शिव'} तत्त्वदिवसे ${credentials.vasara_name || 'अं प्रह्लादानन्दनाथ'} वासरे ${credentials.dina_nitya?.letter || 'अ'} दिननित्यायां ${credentials.paksha || 'शुक्ल पक्ष'} पक्षे ${credentials.tithi_name || 'प्रतिपदा'} तिथि नित्यायां .... गोत्रोद्भवः/ गोत्रोद्भवा ..... देवशर्मा/ देवी/ दासः/ दासी ........`;
    
    setDateSankalpa(sankalpaText);
    setSankalpaPending(false);
    return sankalpaText;
  };

  const fetchCurrentDailyTithis = async (location = selectedCity, method = tithiMethod, forceKulaType = null) => {
    try {
      // Use provided kula type or current state (already in correct format)
      const currentKula = forceKulaType || kulaType;
      const kulaTypeForAPI = currentKula || 'shri_kula'; // Default to shri_kula
      
      console.log(`🔄 fetchCurrentDailyTithis called with kulaType: ${currentKula} -> ${kulaTypeForAPI}`);
      
      const response = await axios.get(`${API}/devimana/current-tithis?location=${location}&method=${method}&kula_type=${kulaTypeForAPI}`);
      if (response.data.success) {
        setCurrentDailyTithis(response.data.daily_tithis);
        console.log(`✅ Daily tithis updated with ${kulaTypeForAPI}:`, response.data.daily_tithis?.tithis?.[0]?.tithi_name);
        
        // Also get the timezone for the current location
        const cityResponse = await axios.get(`${API}/cities/exact/${location}`);
        if (cityResponse.data.success) {
          setCurrentLocationTimezone(cityResponse.data.city.timezone);
        }
      } else {
        setCurrentDailyTithis(null);
      }
    } catch (error) {
      console.error('Failed to fetch current daily tithis:', error);
      setCurrentDailyTithis(null);
    }
  };

  const searchLookupCities = async (query) => {
    try {
      const response = await axios.get(`${API}/cities/search?q=${encodeURIComponent(query)}&limit=8`);
      if (response.data.success) {
        setLookupLocationResults(response.data.cities);
      } else {
        setLookupLocationResults([]);
      }
    } catch (error) {
      console.error('Failed to search cities:', error);
      setLookupLocationResults([]);
    }
  };

  const selectLookupLocation = (city) => {
    setLookupLocationData(city);
    setLookupLocationQuery(city.name);
    setLookupLocationResults([]);
    // Refresh date credentials with new location
    fetchDateCredentials(lookupDate);
  };

  const fetchSunriseSunset = async (cityName = selectedCity) => {
    try {
      const coordinates = majorCities[cityName];
      if (!coordinates) return;

      const today = new Date();
      const response = await fetch(
        `https://api.sunrise-sunset.org/json?lat=${coordinates.lat}&lng=${coordinates.lon}&date=${today.toISOString().split('T')[0]}&formatted=0`
      );
      const data = await response.json();
      
      if (data.status === 'OK') {
        setSunriseSunset({
          sunrise: new Date(data.results.sunrise).toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          }),
          sunset: new Date(data.results.sunset).toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          }),
          location: cityName
        });
      }
    } catch (error) {
      console.error('Failed to fetch sunrise/sunset:', error);
      setSunriseSunset(null);
    }
  };

  const updateGhatika = () => {
    // Only update if we have initial ghatika data from backend
    if (!ghatika || !ghatika.current_ghatika) return;
    
    const now = new Date();
    const totalSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
    const totalMinutes = Math.floor(totalSeconds / 60);
    
    // Each Ghatika = 24 minutes = 1440 seconds
    const secondsPerGhatika = 24 * 60;
    
    // Use backend's Ghatika number but calculate real-time progress within it
    const currentGhatika = ghatika.current_ghatika;
    
    // Calculate which second we should be at in this Ghatika
    const ghatikaStartSeconds = (currentGhatika - 1) * secondsPerGhatika;
    const secondsInCurrentGhatika = totalSeconds - ghatikaStartSeconds;
    
    // Handle Ghatika transitions (when we move to next Ghatika)
    if (secondsInCurrentGhatika >= secondsPerGhatika || secondsInCurrentGhatika < 0) {
      // Fetch fresh data from backend when Ghatika changes
      fetchCurrentDate();
      return;
    }
    
    const minuteInGhatika = Math.floor(secondsInCurrentGhatika / 60);
    const secondInGhatika = secondsInCurrentGhatika % 60;
    const ghatikaProgress = (secondsInCurrentGhatika / secondsPerGhatika) * 100;
    
    setGhatika(prev => ({
      ...prev,
      current_minute_in_ghatika: minuteInGhatika,
      current_second_in_ghatika: secondInGhatika,
      ghatika_progress: ghatikaProgress,
      total_minutes_today: totalMinutes,
      total_seconds_today: totalSeconds
    }));
  };

  useEffect(() => {
    fetchCurrentDate();
    fetchEvents();
    fetchMajorCities();
    
    // Apply saved Kula type on startup
    const applyKulaType = async () => {
      try {
        await axios.post(`${API}/devimana/kula-type?kula_type=${kulaType}`);
      } catch (error) {
        console.error('Failed to apply Kula type on startup:', error);
      }
    };
    applyKulaType();
    
    // Fetch Dina Nitya
    fetchDinaNitya(tantraType);
    
    // Fetch current daily tithis
    fetchCurrentDailyTithis();
    
    // Fetch initial date credentials for today
    fetchDateCredentials(lookupDate);
    
    // Update Ghatika every second for real-time updates (after backend data loads)
    const interval = setInterval(updateGhatika, 1000);
    return () => clearInterval(interval);
  }, []);

  // Start real-time Ghatika updates once we have initial data
  useEffect(() => {
    if (ghatika && ghatika.current_ghatika) {
      updateGhatika();
    }
  }, [ghatika?.current_ghatika]);

  useEffect(() => {
    // Load traditional tithi, weekly data, sunrise/sunset, and daily tithis when cities are loaded
    if (Object.keys(majorCities).length > 0 && selectedCity) {
      fetchTraditionalTithi(selectedCity, tithiMethod, kulaType);
      fetchWeeklyTithis(currentWeekStart, tithiMethod, kulaType);
      fetchSunriseSunset(selectedCity);
      fetchCurrentDailyTithis(selectedCity, tithiMethod, kulaType);
    }
  }, [majorCities, selectedCity, tithiMethod, implementationType]);

  // Close location search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLocationResults && !event.target.closest('.relative')) {
        setShowLocationResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLocationResults]);

  // Initialize location search with saved city
  useEffect(() => {
    if (selectedCity && !locationSearchQuery) {
      setLocationSearchQuery(selectedCity);
    }
    // If we have saved city data, restore sunrise/sunset for it
    if (selectedCityData) {
      fetchSunriseSunsetFromCoords(selectedCityData.lat, selectedCityData.lon, selectedCityData.name);
    }
  }, [selectedCity]);

  // Initialize settings from saved preferences
  useEffect(() => {
    setCalculationMethod(tithiMethod);
    setUseAuthenticSiddhanta(implementationType === 'authentic');
  }, [tithiMethod, implementationType]);

  // Watch for tantraType and kulaType changes and refetch all data
  useEffect(() => {
    console.log(`🔄 Kula/Tantra change detected - kulaType: ${kulaType}, tantraType: ${tantraType}`);
    
    // Force refresh all Kula-dependent data
    if (selectedCity) {
      console.log('📱 Updating Dina Nitya for new Kula type');
      fetchDinaNitya(tantraType, kulaType);
      
      console.log('🌙 Updating daily tithis for new Kula type');
      fetchCurrentDailyTithis(selectedCity, tithiMethod, kulaType);
    }
    
    // Refresh current date for Masa/Vasara/all hierarchy names based on kula
    console.log('📅 Updating current date data for new Kula type');
    fetchCurrentDate(kulaType);
    
    // Also refresh date lookup credentials if a date is selected
    if (lookupDate) {
      console.log('🔍 Updating date lookup data for new Kula type');
      fetchDateCredentials(lookupDate);
    }
    
    console.log('✅ All Kula-dependent data update calls initiated');
  }, [tantraType, selectedCity, tithiMethod, kulaType]);
  // Watch for authentication changes to refresh events
  useEffect(() => {
    console.log('🔄 Auth status changed:', isAuthenticated, user?.username);
    if (isAuthenticated) {
      // User logged in - fetch their events
      fetchEvents();
    } else {
      // User logged out - clear events
      setEvents([]);
    }
  }, [isAuthenticated, user]);

  // Mobile App Initialization
  useEffect(() => {
    const initMobileApp = async () => {
      console.log('📱 Initializing mobile app...');
      
      // Get device information
      const deviceInfo = await MobileService.getDeviceInfo();
      setDeviceInfo(deviceInfo);
      setIsMobile(MobileService.isMobile());
      
      // Setup network monitoring
      const networkStatus = await MobileService.getNetworkStatus();
      setIsOnline(networkStatus.connected);
      
      // Setup app state listeners
      MobileService.setupAppStateListeners();
      
      // Load offline data if available
      await OfflineService.loadCachedData();
      
      // Request notification permissions
      if (MobileService.isMobile()) {
        await NotificationService.init();
        
        // Schedule daily Sankalpa reminder (6 AM)
        await NotificationService.scheduleDailySankalpReminder('06:00');
      }
      
      console.log('📱 Mobile app initialization complete');
      console.log('🔧 Device:', deviceInfo?.platform, deviceInfo?.model);
      console.log('🌐 Network:', networkStatus.connected ? 'Online' : 'Offline');
    };

    initMobileApp();

    // Network change listener
    const handleNetworkChange = (event) => {
      setIsOnline(event.detail.isOnline);
      console.log('📡 Network changed:', event.detail.isOnline ? 'Online' : 'Offline');
    };

    // App resume listener
    const handleAppResume = () => {
      console.log('📱 App resumed - refreshing data');
      if (isOnline) {
        fetchCurrentDate();
        if (selectedCity) {
          fetchDinaNitya(tantraType);
        }
      }
    };

    window.addEventListener('network-change', handleNetworkChange);
    window.addEventListener('app-resume', handleAppResume);

    return () => {
      window.removeEventListener('network-change', handleNetworkChange);
      window.removeEventListener('app-resume', handleAppResume);
    };
  }, []);

  // PWA Installation Detection
  useEffect(() => {
    // Check if app is already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        window.navigator.standalone || 
                        document.referrer.includes('android-app://');

    if (isStandalone) {
      console.log('📱 App already installed, hiding install button');
      setShowInstallButton(false);
      return;
    }

    // Show install button by default (for manual installation instructions)
    setShowInstallButton(true);

    // Listen for beforeinstallprompt event (for native PWA install)
    const handleBeforeInstallPrompt = (e) => {
      console.log('📱 PWA install prompt available');
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('📱 PWA has been installed');
      setDeferredPrompt(null);
      setShowInstallButton(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // PWA Installation Handler
  const handleInstallApp = async () => {
    if (deferredPrompt) {
      try {
        // Show the native install prompt
        deferredPrompt.prompt();
        
        // Wait for the user's response
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          console.log('📱 User accepted PWA installation');
          toast.success('🎉 App installation started! Look for the Devimana Calendar icon on your home screen.');
        } else {
          console.log('📱 User dismissed PWA installation');
          toast.info('You can install this app anytime from your browser menu.');
        }
        
        // Reset the deferred prompt
        setDeferredPrompt(null);
      } catch (error) {
        console.error('📱 Error installing PWA:', error);
        toast.error('Installation failed. Please try the manual method below.');
      }
    } else {
      // Detect platform and provide specific instructions
      const userAgent = navigator.userAgent.toLowerCase();
      const isIOS = /iphone|ipad|ipod/.test(userAgent);
      const isAndroid = /android/.test(userAgent);
      const isChrome = /chrome/.test(userAgent);
      const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);

      let instructions = '';
      
      if (isIOS && isSafari) {
        instructions = '📱 To install on iPhone/iPad:\n\n1. Tap the Share button (⬆️)\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" to install the app';
      } else if (isAndroid && isChrome) {
        instructions = '📱 To install on Android:\n\n1. Tap the browser menu (⋮)\n2. Tap "Add to Home Screen"\n3. Tap "Add" to install the app';
      } else {
        instructions = '📱 To install this app:\n\n• Look for "Add to Home Screen" or "Install" option in your browser menu\n• The app will appear on your home screen with the Devimana Calendar icon';
      }

      toast.info(instructions, { duration: 10000 });
    }
  };

  // Dedicated effect for Kula type changes - CRITICAL for proper Sanskrit name updates
  useEffect(() => {
    if (kulaType) {
      console.log(`🕉️ KULA TYPE CHANGED TO: ${kulaType.toUpperCase()}`);
      console.log('🔄 Initiating comprehensive Sanskrit name updates...');
      
      // Save to localStorage for persistence
      localStorage.setItem('devimana_kula_type', kulaType);
      
      // Force refresh ALL components that display Sanskrit names with the NEW kula type
      const refreshAllKulaData = async () => {
        try {
          console.log(`🚀 FORCING REFRESH WITH NEW KULA TYPE: ${kulaType}`);
          
          // 1. Update current date (Masa, Vasara, all hierarchy names)
          await fetchCurrentDate(kulaType);
          
          // 2. Update Dina Nitya names
          if (selectedCity) {
            await fetchDinaNitya(tantraType, kulaType);
            
            // 3. Update daily tithis (tithi names)
            await fetchCurrentDailyTithis(selectedCity, tithiMethod, kulaType);
          }
          
          // 4. Update date lookup if active
          if (lookupDate) {
            await fetchDateCredentials(lookupDate);
          }
          
          console.log(`✅ KULA UPDATE COMPLETE: All Sanskrit names now follow ${kulaType.toUpperCase()} tradition`);
        } catch (error) {
          console.error('❌ Error updating Kula-dependent data:', error);
        }
      };
      
      // Execute with slight delay to ensure proper state updates
      setTimeout(refreshAllKulaData, 100);
    }
  }, [kulaType]);

  // Force re-render when language changes
  useEffect(() => {
    // This will trigger a re-render of all components when language changes
    console.log('Language changed to:', language);
  }, [language]);

  useEffect(() => {
    // Initialize viewing states when current date is loaded
    if (currentDate?.date) {
      setViewingMasa({
        yuga: currentDate.date.yuga,
        parivritti: currentDate.date.parivritti,
        varsha: currentDate.date.varsha,
        masa: currentDate.date.masa
      });
      setViewingVarsha({
        yuga: currentDate.date.yuga,
        parivritti: currentDate.date.parivritti,
        varsha: currentDate.date.varsha
      });
    }
  }, [currentDate]);

  // Click handlers for hierarchical navigation
  const handleMasaClick = (masa) => {
    setViewingMasa(masa);
    setActiveView("month");
  };

  const handleGregorianMonthClick = (month, year) => {
    const newDate = new Date(year, month, 1);
    setGregorianDate(newDate);
    setActiveView("month");
  };

  const handleDayClick = (dayInfo, isDevimana = true) => {
    if (isDevimana) {
      setSelectedDevimanaDate(dayInfo);
    } else {
      setSelectedDate(dayInfo);
    }
    setActiveView("dashboard");
  };

  const calculateKaliDayFromDevimana = (devimanaDate) => {
    // Calculate Kali Yuga day number from Devimana date
    return ((devimanaDate.yuga - 1) * 746496) + 
           ((devimanaDate.parivritti - 1) * 20736) +
           ((devimanaDate.varsha - 1) * 576) +
           ((devimanaDate.masa - 1) * 36) +
           devimanaDate.divasa;
  };

  const goToToday = () => {
    setSelectedDate(null);
    setSelectedDevimanaDate(null);
    setActiveView("dashboard");
  };

  // Navigation functions
  const navigateDevimanaMonth = (direction) => {
    if (!viewingMasa) return;
    
    let newMasa = viewingMasa.masa + direction;
    let newVarsha = viewingMasa.varsha;
    let newParivritti = viewingMasa.parivritti;
    let newYuga = viewingMasa.yuga;

    if (newMasa > 16) {
      newMasa = 1;
      newVarsha++;
      if (newVarsha > 36) {
        newVarsha = 1;
        newParivritti++;
        if (newParivritti > 36) {
          newParivritti = 1;
          newYuga++;
        }
      }
    } else if (newMasa < 1) {
      newMasa = 16;
      newVarsha--;
      if (newVarsha < 1) {
        newVarsha = 36;
        newParivritti--;
        if (newParivritti < 1) {
          newParivritti = 36;
          newYuga--;
        }
      }
    }

    setViewingMasa({
      yuga: newYuga,
      parivritti: newParivritti,
      varsha: newVarsha,
      masa: newMasa
    });
  };

  const navigateDevimanaYear = (direction) => {
    if (!viewingVarsha) return;
    
    let newVarsha = viewingVarsha.varsha + direction;
    let newParivritti = viewingVarsha.parivritti;
    let newYuga = viewingVarsha.yuga;

    if (newVarsha > 36) {
      newVarsha = 1;
      newParivritti++;
      if (newParivritti > 36) {
        newParivritti = 1;
        newYuga++;
      }
    } else if (newVarsha < 1) {
      newVarsha = 36;
      newParivritti--;
      if (newParivritti < 1) {
        newParivritti = 36;
        newYuga--;
      }
    }

    setViewingVarsha({
      yuga: newYuga,
      parivritti: newParivritti,
      varsha: newVarsha
    });
  };

  const navigateGregorianMonth = (direction) => {
    const newDate = new Date(gregorianDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setGregorianDate(newDate);
  };

  const navigateGregorianYear = (direction) => {
    const newDate = new Date(gregorianDate);
    newDate.setFullYear(newDate.getFullYear() + direction);
    setGregorianDate(newDate);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-100 flex items-center justify-center">
        <div className="animate-pulse flex items-center space-x-4">
          <Calendar className="h-8 w-8 text-amber-600" />
          <span className="text-xl font-medium text-amber-800">Loading Complete Calendar...</span>
        </div>
      </div>
    );
  }

  if (!currentDate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-100 flex items-center justify-center">
        <Card className="p-8 shadow-xl">
          <div className="text-center">
            <Calendar className="h-16 w-16 text-amber-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Calendar Unavailable</h2>
            <p className="text-gray-600">Unable to load the Devimana Ashtanga calendar.</p>
            <Button onClick={fetchCurrentDate} className="mt-4">
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const { date, time, kali_yuga_day } = currentDate;

  const renderDashboard = () => {
    // Determine which date to display
    const displayDate = selectedDevimanaDate || (selectedDate ? null : date);
    const displayKaliDay = selectedDevimanaDate ? 
      calculateKaliDayFromDevimana(selectedDevimanaDate) : 
      (selectedDate ? null : kali_yuga_day);
    
    return (
      <div>
        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3 mb-6" data-testid="dashboard-actions">
          <EventPlanner currentDate={currentDate} onEventCreated={fetchEvents} />
          <button 
            onClick={async () => {
              if (isAuthenticated) {
                await generateCurrentSankalpa();
                setShowSankalpa(true);
              } else {
                setShowLoginModal(true);
              }
            }}
            className={`app-button app-button-secondary ${sankalpaPending ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid="generate-sankalpa-button"
            disabled={sankalpaPending}
          >
            <Star className="h-4 w-4" />
            <span>{sankalpaPending ? 'Generating...' : t.generateSankalpa}</span>
            {!isAuthenticated && <span className="text-xs opacity-75">(Login)</span>}
          </button>
          <button 
            onClick={() => setShowYogaChecker(true)}
            className="app-button app-button-secondary"
            data-testid="check-yogas-button"
          >
            <Star className="h-4 w-4" />
            <span>Check Yogas</span>
          </button>
          <button 
            onClick={() => setActiveView("week")}
            className="app-button"
            data-testid="view-week-button"
          >
            <CalendarDays className="h-4 w-4" />
            <span>Week Views</span>
          </button>
        </div>

        {/* Calendar Integration Test (only show on mobile) */}
        {MobileService.isMobile() && (
          <div className="mb-4">
            <button 
              onClick={async () => {
                if (!isAuthenticated) {
                  setShowLoginModal(true);
                  return;
                }
                
                // Create a test event with calendar sync
                const testEvent = {
                  id: 'test-' + Date.now(),
                  title: '🕉️ Daily Sankalpa Reminder',
                  description: 'Time for your daily Devimana meditation and prayer',
                  calendar_type: 'gregorian',
                  gregorian_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow
                  tithi_info: {
                    tithi_name: traditionalTithiData?.tithi_name || 'प्रतिपदा',
                    paksha: traditionalTithiData?.paksha || 'शुक्ल पक्ष',
                    tithi_progress: traditionalTithiData?.tithi_progress || 0.5
                  }
                };

                try {
                  // Test calendar integration
                  const calendarResult = await CalendarService.addEventToCalendar(testEvent);
                  
                  if (calendarResult.success) {
                    toast.success(`📅 Test event added to ${calendarResult.calendarName}`);
                    
                    // Test notification scheduling
                    const notificationResult = await NotificationService.scheduleEventReminder(testEvent);
                    
                    if (notificationResult.success) {
                      const reminderDate = new Date(notificationResult.reminderTime);
                      toast.success(`🔔 Reminder set for ${reminderDate.toLocaleDateString()} at ${reminderDate.toLocaleTimeString()}`);
                    }
                  } else {
                    toast.error(`❌ Calendar test failed: ${calendarResult.error}`);
                  }
                } catch (error) {
                  console.error('Test integration error:', error);
                  toast.error('Integration test failed');
                }
              }}
              className="app-button w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
              disabled={!isAuthenticated}
            >
              <Bell className="h-4 w-4" />
              <span>Test Calendar & Notifications</span>
              {!isAuthenticated && <span className="text-xs opacity-75">(Login)</span>}
            </button>
          </div>
        )}

        {/* Main Calendar Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6 lg:gap-8">
          
          {/* Left Column - Main Date Display */}
          <div className="space-y-3 sm:space-y-4 md:space-y-5 lg:space-y-6">
            {/* Current Date Display */}
            <div className="app-card">
              <div className="app-card-header">
                <Calendar className="h-5 w-5 inline mr-2" />
                <span>{t.currentDate}</span>
              </div>
              <div className="app-card-content">
                {selectedDate ? (
                  /* Gregorian Date Display */
                  <div className="text-center py-8">
                    <h2 className="text-4xl font-bold text-green-900 mb-4">
                      {selectedDate.date.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </h2>
                    <p className="text-green-700 text-lg">Selected Gregorian Date</p>
                    <div className="mt-6 grid grid-cols-3 gap-4 max-w-lg mx-auto">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-800">{selectedDate.day}</div>
                        <div className="text-sm text-gray-600">Day</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-800">{selectedDate.month + 1}</div>
                        <div className="text-sm text-gray-600">Month</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-800">{selectedDate.year}</div>
                        <div className="text-sm text-gray-600">Year</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3 md:gap-4 lg:gap-6">
                  
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-amber-800 mb-1">{displayDate?.yuga || date?.yuga}</div>
                    <div className="text-xs sm:text-sm font-medium text-amber-600">युग</div>
                    <div className="text-xs text-purple-700 font-medium">{displayDate?.yuga_name || date?.yuga_name}</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-orange-800 mb-1">{displayDate?.parivritti || date?.parivritti}</div>
                    <div className="text-xs sm:text-sm font-medium text-orange-600">परिवृत्ति</div>
                    <div className="text-xs text-purple-700 font-medium">{displayDate?.parivritti_name || date?.parivritti_name}</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-yellow-800 mb-1">{displayDate?.varsha || date?.varsha}</div>
                    <div className="text-xs sm:text-sm font-medium text-yellow-600">वर्ष</div>
                    <div className="text-xs text-purple-700 font-medium">{displayDate?.varsha_name || date?.varsha_name}</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-red-800 mb-1">{displayDate?.masa || date?.masa}</div>
                    <div className="text-xs sm:text-sm font-medium text-red-600">मास</div>
                    <div className="text-xs text-purple-700 font-medium">{displayDate?.masa_name || date?.masa_name}</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-indigo-800 mb-1">{displayDate?.divasa || date?.divasa}</div>
                    <div className="text-xs sm:text-sm font-medium text-indigo-600">दिवस</div>
                    <div className="text-xs text-purple-700 font-medium">{displayDate?.divasa_name || date?.divasa_name}</div>
                  </div>
                  
                  <div className="text-center sm:col-span-2 md:col-span-2">
                    <div className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-800 mb-1">{displayDate?.vasara || date?.vasara}</div>
                    <div className="text-xs sm:text-sm font-medium text-purple-600">वासर</div>
                    <div className="text-xs text-gray-500">Vasara</div>
                  </div>
                  
                </div>
                )}
                
                {/* Selected Date Info */}
                {selectedDevimanaDate && (
                  <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                    <h4 className="font-semibold text-amber-900 mb-2">Selected Devimana Date Details</h4>
                    <p className="text-sm">
                      <strong>{selectedDevimanaDate.divasa_name}</strong> - दिवस {selectedDevimanaDate.divasa} of मास {selectedDevimanaDate.masa_name}
                    </p>
                  </div>
                )}
                
                <Separator className="my-6" />
                
                {/* Enhanced Vasara Name Display */}
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">{t.todaysVasara}</h3>
                  <div className="text-2xl font-bold text-purple-800 mb-2" style={{ fontFamily: 'serif' }}>
                    {date.vasara_name}
                  </div>
                  <Badge variant="secondary" className="text-sm">
                    {date.vasara} of 9 Vasaras
                  </Badge>
                </div>

                <Separator className="my-6" />

                {/* Dina Nitya Display */}
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">दिन नित्या (Dina Nitya)</h3>
                  {dinaNitya ? (
                    <div>
                      <div className="text-4xl font-bold text-indigo-800 mb-2">
                        {dinaNitya.letter}
                      </div>
                      <div className="text-sm font-semibold text-indigo-700 mb-2">
                        {dinaNitya.cycle_name}
                      </div>
                      <Badge variant="outline" className="text-xs mb-2">
                        Position {dinaNitya.position} of {dinaNitya.total_positions}
                      </Badge>
                      
                      {/* Additional Info for Paramananda Tantra */}
                      {dinaNitya.tantra_type === 'paramananda' && dinaNitya.paksha && (
                        <div className="mt-2 p-2 bg-indigo-50 rounded-lg text-xs text-indigo-700">
                          {dinaNitya.tithi_name} {dinaNitya.paksha}
                        </div>
                      )}
                      
                      <div className="mt-2 text-xs text-gray-500">
                        {dinaNitya.tantra_type === 'saubhagya' ? 'सौभाग्य तन्त्र' : 'परमानन्द तन्त्र'}
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-500 text-sm">Loading Dina Nitya...</div>
                  )}
                </div>
                
              </div>
            </div>
          </div>

          {/* Right Column - Time & Additional Info */}
          <div className="space-y-3 sm:space-y-4 md:space-y-5 lg:space-y-6">
            
            {/* Ghatika Time */}
            <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
              <CardHeader className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-t-lg p-3 sm:p-4">
                <CardTitle className="text-sm sm:text-lg font-medium flex items-center space-x-1 sm:space-x-2">
                  <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="text-xs sm:text-base">{t.currentGhatika}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 md:p-5 lg:p-6">
                {ghatika && (
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-indigo-800 mb-1 sm:mb-2">
                      {ghatika.current_ghatika}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">
                      {t.ghatikaOf60.replace('{current}', ghatika.current_ghatika)}
                    </div>
                    
                    {/* Real-time progress within current Ghatika */}
                    <div className="space-y-1 sm:space-y-2 mb-2 sm:mb-4">
                      <div className="text-xs text-gray-500">
                        Minute {ghatika.current_minute_in_ghatika + 1} of 24
                      </div>
                      <div className="text-xs text-gray-400">
                        Second {ghatika.current_second_in_ghatika || 0} of 60
                      </div>
                      <div className="text-xs font-medium text-indigo-600">
                        {(ghatika.ghatika_progress || 0).toFixed(1)}% Complete
                      </div>
                    </div>
                    
                    {/* Animated progress bar */}
                    <div className="mt-2 sm:mt-4 bg-gray-200 rounded-full h-2 sm:h-3">
                      <div 
                        className="bg-gradient-to-r from-indigo-600 to-blue-600 h-2 sm:h-3 rounded-full transition-all duration-1000"
                        style={{ width: `${ghatika.ghatika_progress || 0}%` }}
                      ></div>
                    </div>
                    
                    {/* Time display */}
                    <div className="mt-2 sm:mt-3 text-xs text-gray-500">
                      {ghatika.current_minute_in_ghatika || 0}:{String(ghatika.current_second_in_ghatika || 0).padStart(2, '0')} / 24:00
                    </div>

                    {/* Ghatikodaya Display */}
                    <Separator className="my-4 sm:my-6" />
                    <div className="mt-4 sm:mt-6">
                      <h3 className="text-sm sm:text-base font-semibold text-gray-700 mb-2">घटिकोदय (Ghatikodaya)</h3>
                      {currentDate.ghatikodaya ? (
                        <div>
                          <div className="text-3xl sm:text-4xl font-bold text-teal-800 mb-2">
                            {currentDate.ghatikodaya}
                          </div>
                          <Badge variant="outline" className="text-xs">
                            Based on Kali Yuga Divasa
                          </Badge>
                        </div>
                      ) : (
                        <div className="text-gray-500 text-xs sm:text-sm">Loading...</div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sunrise Sunset Info */}
            <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
              <CardHeader className="bg-gradient-to-r from-orange-500 to-yellow-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center space-x-2">
                  <Sunrise className="h-5 w-5" />
                  <span>{t.sunriseSunset}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {sunriseSunset ? (
                  <div className="space-y-4">
                    <div className="text-center">
                      <div className="text-sm text-gray-600 mb-3">
                        {t.location}: <span className="font-semibold text-orange-700">{sunriseSunset.location}</span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 bg-orange-50 rounded-lg">
                        <div className="flex items-center justify-center space-x-2 mb-2">
                          <Sunrise className="h-4 w-4 text-orange-600" />
                          <span className="text-sm font-medium text-orange-700">{t.sunrise}</span>
                        </div>
                        <div className="text-lg font-bold text-orange-800">
                          {sunriseSunset.sunrise}
                        </div>
                      </div>
                      
                      <div className="text-center p-3 bg-yellow-50 rounded-lg">
                        <div className="flex items-center justify-center space-x-2 mb-2">
                          <Moon className="h-4 w-4 text-yellow-600" />
                          <span className="text-sm font-medium text-yellow-700">{t.sunset}</span>
                        </div>
                        <div className="text-lg font-bold text-yellow-800">
                          {sunriseSunset.sunset}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-4">
                    Loading sunrise & sunset times...
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Daily Tithis Section for Current Date */}
            {currentDailyTithis && (
              <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
                <CardHeader className="bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-t-lg">
                  <CardTitle className="flex items-center space-x-2">
                    <Moon className="h-5 w-5" />
                    <span>Today's Tithis (Sunrise to Sunrise)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    
                    {/* Location and Method Info */}
                    <div className="text-center bg-purple-50 rounded-lg p-3">
                      <div className="text-sm text-purple-700 font-medium">
                        Location: {currentDailyTithis.location?.name} | Method: {currentDailyTithis.method}
                      </div>
                    </div>

                    {/* Sunrise Times */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                      <div className="text-center bg-yellow-50 rounded p-2">
                        <div className="text-xs text-yellow-700">Sunrise</div>
                        <div className="text-sm font-semibold text-yellow-800">
                          {new Date(currentDailyTithis.sunrise).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: currentLocationTimezone,
                            timeZoneName: 'short'
                          })}
                        </div>
                      </div>
                      <div className="text-center bg-orange-50 rounded p-2">
                        <div className="text-xs text-orange-700">Next Sunrise</div>
                        <div className="text-sm font-semibold text-orange-800">
                          {new Date(currentDailyTithis.next_sunrise).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: currentLocationTimezone,
                            timeZoneName: 'short'
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Tithis List */}
                    {currentDailyTithis.tithis && currentDailyTithis.tithis.length > 0 ? (
                      currentDailyTithis.tithis.map((tithi, index) => (
                        <Card key={index} className="bg-gradient-to-r from-purple-50 to-violet-50 border-purple-200">
                          <CardContent className="p-4">
                            <div className="flex flex-col space-y-2">
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="text-lg font-bold text-purple-800">{tithi.tithi_name}</div>
                                  <div className="text-sm text-purple-600">{tithi.paksha}</div>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-2 mt-2">
                                <div>
                                  <div className="text-xs text-gray-600 font-medium">Start Time:</div>
                                  <div className="text-xs font-mono text-gray-800">
                                    {new Date(tithi.start).toLocaleString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      timeZone: currentLocationTimezone,
                                      timeZoneName: 'short'
                                    })}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xs text-gray-600 font-medium">End Time:</div>
                                  <div className="text-xs font-mono text-gray-800">
                                    {new Date(tithi.end).toLocaleString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      timeZone: currentLocationTimezone,
                                      timeZoneName: 'short'
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <div className="text-center text-gray-500 py-2">
                        No tithi information available.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Dina Nitya Info moved to Complete Current Date section */}

            {/* Traditional Tithi Information */}
            <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
              <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <Star className="h-5 w-5" />
                    <span>पारम्परिक तिथि</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => fetchTraditionalTithi(selectedCity, calculationMethod, kulaType)}
                    className="text-white hover:bg-white/20"
                  >
                    <MapPin className="h-4 w-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex items-center justify-center space-x-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <Switch 
                      checked={showTraditional} 
                      onCheckedChange={setShowTraditional}
                    />
                    <Label>Show Traditional Methods</Label>
                  </div>
                  <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                    <Label className="text-sm">
                      {t[tithiMethod.replace('_siddhanta', 'Siddhanta')]} ({implementationType === 'authentic' ? t.authentic : t.modern})
                    </Label>
                  </div>
                </div>
                {/* Method Toggle */}
                <div className="mb-4 text-center">
                  <Button
                    variant="outline"
                    onClick={() => setActiveView('settings')}
                    size="sm"
                  >
                    Configure Method in Settings
                  </Button>
                </div>

                <div className="mb-4">
                  <Label className="text-sm text-gray-600 mb-2 block">Location:</Label>
                  <div className="relative">
                    <input
                      type="text"
                      value={locationSearchQuery}
                      onChange={(e) => {
                        setLocationSearchQuery(e.target.value);
                        searchLocations(e.target.value);
                      }}
                      onFocus={() => {
                        if (locationSearchResults.length > 0) {
                          setShowLocationResults(true);
                        }
                      }}
                      placeholder="Search for a city..."
                      className="w-full p-2 border rounded text-sm pr-8"
                    />
                    {isSearchingLocation && (
                      <div className="absolute right-2 top-2">
                        <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                      </div>
                    )}
                    
                    {showLocationResults && locationSearchResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded shadow-lg max-h-60 overflow-y-auto">
                        {locationSearchResults.map((city, index) => (
                          <div
                            key={index}
                            onClick={() => selectLocation(city)}
                            className="p-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 text-sm"
                          >
                            <div className="font-medium">{city.name}</div>
                            <div className="text-xs text-gray-500">
                              {city.lat.toFixed(4)}, {city.lon.toFixed(4)} • {city.timezone}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {selectedCityData && (
                    <div className="mt-2 p-2 bg-green-50 rounded text-xs">
                      <strong>Selected:</strong> {selectedCityData.name}<br />
                      <span className="text-gray-600">
                        {selectedCityData.lat.toFixed(4)}, {selectedCityData.lon.toFixed(4)}
                      </span>
                    </div>
                  )}
                </div>
                
                {traditionalTithiData ? (
                  <div className="text-center">
                    <div className="text-lg font-bold text-purple-800 mb-2">
                      {traditionalTithiData.tithi_name}
                    </div>
                    <div className="text-sm text-purple-600 mb-3">
                      {traditionalTithiData.paksha}
                    </div>
                    <div className="text-xs text-gray-600 mb-2">
                      Tithi {traditionalTithiData.adjusted_tithi} • Progress: {(traditionalTithiData.tithi_progress * 100).toFixed(1)}%
                    </div>
                    <div className="text-xs text-gray-500 mb-2">
                      Method: {traditionalTithiData.method}
                    </div>
                    <div className="text-xs text-gray-500">
                      Ends: {new Date(traditionalTithiData.tithi_end_time).toLocaleTimeString()}
                    </div>
                    <div className="mt-3 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(traditionalTithiData.tithi_progress * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-500">
                    <Star className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Click to calculate traditional tithi</p>
                  </div>
                )}
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    );
  };

  const renderDualWeekView = () => {
    
    const vasaraNames = [
      "अं प्रह्लादानन्दनाथ", "लृं सकलानन्दनाथ", "कं कुमारानन्दनाथ",
      "चं वशिष्ठानन्दनाथ", "टं क्रोधानन्दनाथ", "तं सुरानन्दनाथ",
      "पं ध्यानानन्दनाथ", "यं बोधानन्दनाथ", "षं शुकानन्दनाथ"
    ];

    const gregorianDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const today = new Date();
    const currentDayOfWeek = today.getDay();

    try {
      return (
        <div className="space-y-8" data-testid="dual-week-view">
          {/* Week Navigation */}
        <Card className="shadow-xl bg-white/80 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center justify-between">
              <span>Week View with Tithi Details</span>
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => {
                    const prevWeek = new Date(currentWeekStart);
                    prevWeek.setDate(prevWeek.getDate() - 7);
                    setCurrentWeekStart(prevWeek);
                    fetchWeeklyTithis(prevWeek, tithiMethod, kulaType);
                  }}
                  variant="outline"
                  size="sm"
                  className="bg-white text-emerald-600 border-white hover:bg-emerald-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  onClick={() => {
                    setCurrentWeekStart(new Date());
                    fetchWeeklyTithis(new Date(), tithiMethod, kulaType);
                  }}
                  variant="outline"
                  size="sm"
                  className="bg-white text-emerald-600 border-white hover:bg-emerald-50"
                >
                  Today
                </Button>
                <Button
                  onClick={() => {
                    const nextWeek = new Date(currentWeekStart);
                    nextWeek.setDate(nextWeek.getDate() + 7);
                    setCurrentWeekStart(nextWeek);
                    fetchWeeklyTithis(nextWeek, tithiMethod, kulaType);
                  }}
                  variant="outline"
                  size="sm"
                  className="bg-white text-emerald-600 border-white hover:bg-emerald-50"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Week of {currentWeekStart.toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </h3>
            </div>
          </CardContent>
        </Card>

        {/* Gregorian Week with Tithi Information */}
        <Card className="shadow-xl bg-white/80 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg">
            <CardTitle className="text-xl">Weekly Tithi Calendar</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-7 gap-4 mb-4">
              {weeklyTithis.map((dayData, index) => {
                const date = new Date(currentWeekStart);
                date.setDate(date.getDate() + index);
                const isToday = date.toDateString() === today.toDateString();
                
                return (
                  <Card 
                    key={index} 
                    className={`p-4 text-center transition-all cursor-pointer ${
                      isToday ? 'ring-2 ring-blue-500 bg-blue-50 scale-105' : 'hover:shadow-md hover:scale-102'
                    }`}
                    onClick={() => {
                      // Show detailed tithi information
                      if (dayData) {
                        toast.success(`${dayData.tithi_name} - ${dayData.paksha} (${Math.round(dayData.tithi_progress * 100)}% complete)`);
                      }
                    }}
                    data-testid={`week-day-${index}`}
                  >
                    <div className="text-lg font-bold text-blue-800 mb-1">
                      {date.getDate()}
                    </div>
                    <div className="text-xs text-blue-600 mb-2">
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    {dayData ? (
                      <>
                        <div className="text-xs font-medium text-purple-700 mb-1">
                          {dayData.tithi_name}
                        </div>
                        <div className="text-xs text-gray-600 mb-1">
                          {dayData.paksha}
                        </div>
                        <div className="text-xs text-green-600">
                          {Math.round(dayData.tithi_progress * 100)}%
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1 mt-2">
                          <div 
                            className="bg-purple-600 h-1 rounded-full"
                            style={{ width: `${dayData.tithi_progress * 100}%` }}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-gray-400">Loading...</div>
                    )}
                    {isToday && (
                      <div className="text-xs font-semibold text-blue-700 mt-2 bg-blue-200 rounded px-1">Today</div>
                    )}
                  </Card>
                );
              })}
            </div>
            
            {/* Current Settings Display */}
            <div className="flex justify-center items-center space-x-6 mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="text-sm">
                <strong>{t.calculationMethod}:</strong> {t[tithiMethod.replace('_', '')]} 
              </div>
              <div className="text-sm">
                <strong>{t.implementation}:</strong> {implementationType === 'authentic' ? t.authentic : t.modern}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveView('settings')}
                className="text-xs"
              >
                Change in Settings
              </Button>
            </div>
            
            <div className="text-center text-sm text-gray-600 mt-4">
              Click on any day to see detailed tithi information • Current method: <strong>{calculationMethod}</strong>
            </div>
          </CardContent>
        </Card>

        {/* Devimana Week View */}
        <Card className="shadow-xl bg-white/80 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-lg">
            <CardTitle className="text-xl">देवीमान सप्ताह - Devimana Week (9 Vasaras)</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-3 md:grid-cols-9 gap-3 mb-4">
              {vasaraNames.map((name, index) => (
                <Card 
                  key={index} 
                  className={`p-3 text-center transition-all ${
                    (index + 1) === date.vasara ? 'ring-2 ring-purple-500 bg-purple-50 scale-105' : 'hover:shadow-md'
                  }`}
                  data-testid={`devimana-day-${index + 1}`}
                >
                  <div className="text-lg font-bold text-purple-800 mb-2">{index + 1}</div>
                  <div className="text-xs text-purple-600 mb-1">{name.split(' ')[0]}</div>
                  <div className="text-xs font-medium text-purple-700">{name.split(' ')[1]}</div>
                  {(index + 1) === date.vasara && (
                    <div className="text-xs font-semibold text-purple-700 mt-1 bg-purple-200 rounded px-1">आज</div>
                  )}
                </Card>
              ))}
            </div>
            <div className="text-center text-sm text-gray-600">
              Current Vasara: <strong>{date.vasara_name}</strong> (Day {date.vasara} of 9-day cycle)
            </div>
          </CardContent>
        </Card>
      </div>
    );
    } catch (error) {
      console.error("Error in renderDualWeekView:", error);
      return <div>Error loading week view: {error.message}</div>;
    }
  };

  const handleSaveSettings = () => {
    // Save to localStorage (language is now fixed to English+Sanskrit)
    localStorage.setItem('devimana_tithi_method', tithiMethod);
    localStorage.setItem('devimana_implementation', implementationType);
    localStorage.setItem('devimana_kula_type', kulaType);
    localStorage.setItem('devimana_tantra_type', tantraType);
    
    // Update the global states to reflect changes
    setCalculationMethod(tithiMethod);
    setUseAuthenticSiddhanta(implementationType === 'authentic');
    
    // Refresh data with new settings
    fetchTraditionalTithi(selectedCity, tithiMethod, kulaType);
    fetchWeeklyTithis(currentWeekStart, tithiMethod, kulaType);
    
    // Force refresh ALL Kula-dependent data immediately after settings save
    console.log('🔄 Settings saved - forcing immediate Kula-dependent data refresh');
    fetchCurrentDate(kulaType); // Updates Masa, Vasara, hierarchy names
    fetchDinaNitya(tantraType, kulaType); // Updates Dina Nitya names
    fetchCurrentDailyTithis(selectedCity, tithiMethod, kulaType); // Updates Tithi names
    
    // Also refresh date lookup if active
    if (lookupDate) {
      fetchDateCredentials(lookupDate);
    }
    
    // The useEffect watching tantraType and kulaType will also trigger as backup
    
    toast.success(t.settingsSaved);
  };

  const renderSettingsView = () => {
    return (
      <div className="space-y-8" data-testid="settings-view">
        {/* Header */}
        <Card className="shadow-xl bg-white/80 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center space-x-2">
              <Star className="h-6 w-6" />
              <span>{t.settings}</span>
            </CardTitle>
          </CardHeader>
        </Card>

        {/* Tithi Calculation Preferences */}
        <Card className="shadow-xl bg-white/80 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-t-lg">
            <CardTitle className="text-lg">{t.tithiPreferences}</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {/* Calculation Method Selection */}
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold mb-3 block">{t.calculationMethod}</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card 
                    className={`p-4 cursor-pointer transition-all border-2 ${
                      tithiMethod === 'surya_siddhanta' 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                    onClick={() => setTithiMethod('surya_siddhanta')}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        tithiMethod === 'surya_siddhanta' ? 'bg-purple-500 border-purple-500' : 'border-gray-300'
                      }`} />
                      <div>
                        <div className="font-medium">{t.suryaSiddhanta}</div>
                        <div className="text-xs text-gray-600 mt-1">{t.suryaDescription}</div>
                      </div>
                    </div>
                  </Card>
                  
                  <Card 
                    className={`p-4 cursor-pointer transition-all border-2 ${
                      tithiMethod === 'visuddha_siddhanta' 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                    onClick={() => setTithiMethod('visuddha_siddhanta')}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        tithiMethod === 'visuddha_siddhanta' ? 'bg-purple-500 border-purple-500' : 'border-gray-300'
                      }`} />
                      <div>
                        <div className="font-medium">{t.vishuddhaSiddhanta}</div>
                        <div className="text-xs text-gray-600 mt-1">{t.vishddhaDescription}</div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>

              {/* Implementation Type */}
              <div className="pt-4 border-t">
                <Label className="text-sm font-semibold mb-3 block">{t.implementation}</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card 
                    className={`p-4 cursor-pointer transition-all border-2 ${
                      implementationType === 'traditional' 
                        ? 'border-indigo-500 bg-indigo-50' 
                        : 'border-gray-200 hover:border-indigo-300'
                    }`}
                    onClick={() => setImplementationType('traditional')}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        implementationType === 'traditional' ? 'bg-indigo-500 border-indigo-500' : 'border-gray-300'
                      }`} />
                      <div>
                        <div className="font-medium">{t.modern}</div>
                        <div className="text-xs text-gray-600 mt-1">{t.traditionalDesc}</div>
                      </div>
                    </div>
                  </Card>
                  
                  <Card 
                    className={`p-4 cursor-pointer transition-all border-2 ${
                      implementationType === 'authentic' 
                        ? 'border-indigo-500 bg-indigo-50' 
                        : 'border-gray-200 hover:border-indigo-300'
                    }`}
                    onClick={() => setImplementationType('authentic')}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        implementationType === 'authentic' ? 'bg-indigo-500 border-indigo-500' : 'border-gray-300'
                      }`} />
                      <div>
                        <div className="font-medium">{t.authentic}</div>
                        <div className="text-xs text-gray-600 mt-1">{t.authenticDesc}</div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Language Information */}
        <Card className="shadow-xl bg-white/80 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-t-lg">
            <CardTitle className="text-lg">Language Implementation</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="text-sm text-gray-700">
              <p className="mb-2">
                <strong>Interface:</strong> English for navigation and UI elements
              </p>
              <p className="mb-2">
                <strong>Traditional Terms:</strong> Sanskrit (देवनागरी) for authentic calendar terminology
              </p>
              <p className="text-gray-600 text-xs">
                This combination provides accessibility while preserving traditional Sanskrit nomenclature.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Save Settings Button */}
        <div className="flex justify-center">
          <Button 
            onClick={handleSaveSettings}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-3 rounded-lg font-medium"
          >
            {t.saveSettings}
          </Button>
        </div>
      </div>
    );
  };

  // Month View removed as requested by user

  const renderDualYearView = () => {
    const currentYear = new Date().getFullYear();
    const gregorianMonths = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const devimana16Masas = [
      "अं काली", "आं कपालिनी", "इं कुल्ला", "ईं कुरुकुल्ला", 
      "उं विरोधिनी", "ऊं विप्रचित्ता", "ऋं उग्रा", "ॠं उग्रप्रभा",
      "लृं दीप्ता", "लॄं नीला", "एं घना", "ऐं वलाका", 
      "ओं मात्रा", "औं मुद्रा", "अं मिता", "अः असिता"
    ];

    const tattwaNames = [
      "अं शिव", "कं शक्ति", "खं सदाशिव", "गं ईश्वर", "घं शुद्धविद्या", "ङं माया",
      "चं कला", "छं विद्या", "जं राग", "झं कला", "ञं नियति", "टं पुरुष", 
      "ठं प्रकृति", "डं अहङ्कार", "ढं बुद्धि", "णं मन", "तं श्रोत्र", "थं त्वक्",
      "दं चक्षु", "धं जिह्वा", "नं घ्राण", "पं वाक्", "फं पाणि", "बं पाद",
      "भं पायु", "मं उपस्थ", "यं शब्द", "रं स्पर्श", "लं रूप", "वं रस",
      "शं गन्ध", "षं आकाश", "सं वायु", "हं तेज", "ळं सलिल", "क्षं भूमि"
    ];
    
    return (
      <div className="space-y-8" data-testid="dual-year-view">
        {/* Devimana Varsha View */}
        <Card className="shadow-xl bg-white/80 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center justify-between text-xl">
              <span>देवीमान वर्ष - Devimana Varsha (16 Masas)</span>
              <div className="flex items-center space-x-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigateDevimanaYear(-1)}
                  className="text-white hover:bg-white/20"
                  data-testid="devimana-prev-year"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigateDevimanaYear(1)}
                  className="text-white hover:bg-white/20"
                  data-testid="devimana-next-year"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-red-900 mb-2">
                {viewingVarsha ? tattwaNames[(viewingVarsha.varsha - 1) % 36] : "Loading..."}
              </h3>
              <p className="text-red-700">
                वर्ष {viewingVarsha?.varsha || "..."} of 36 • युग {viewingVarsha?.yuga || "..."} ({viewingVarsha ? tattwaNames[(viewingVarsha.yuga - 1) % 36] : "..."})
              </p>
              <p className="text-sm text-gray-600">
                परिवृत्ति {viewingVarsha?.parivritti || "..."} ({viewingVarsha ? tattwaNames[(viewingVarsha.parivritti - 1) % 36] : "..."})
              </p>
            </div>
            <div className="grid grid-cols-4 gap-4 mb-4">
              {devimana16Masas.map((masaName, index) => {
                const masaNumber = index + 1;
                return (
                  <Card 
                    key={masaNumber} 
                    className={`p-4 text-center transition-all cursor-pointer ${
                      masaNumber === date?.masa && viewingVarsha?.varsha === date?.varsha ? 'ring-2 ring-red-500 bg-red-50 scale-105' : 'hover:shadow-md hover:scale-102'
                    }`}
                    onClick={() => handleMasaClick({
                      yuga: viewingVarsha?.yuga || date?.yuga,
                      parivritti: viewingVarsha?.parivritti || date?.parivritti,
                      varsha: viewingVarsha?.varsha || date?.varsha,
                      masa: masaNumber
                    })}
                    data-testid={`devimana-masa-${masaNumber}`}
                  >
                    <div className="text-xl font-bold text-red-800 mb-2">{masaNumber}</div>
                    <div className="text-sm font-medium text-red-600 mb-1">{masaName}</div>
                    <div className="text-xs text-gray-600">36 दिवस</div>
                    {masaNumber === date?.masa && viewingVarsha?.varsha === date?.varsha && (
                      <div className="text-xs font-semibold text-red-700 mt-2 bg-red-200 rounded px-1">वर्तमान</div>
                    )}
                  </Card>
                );
              })}
            </div>
            <div className="text-center text-sm text-gray-600">
              {viewingVarsha && (
                <span>
                  Current Viewing: <strong>वर्ष {viewingVarsha.varsha} ({tattwaNames[(viewingVarsha.varsha - 1) % 36]})</strong>
                  {date && viewingVarsha.yuga === date.yuga && viewingVarsha.varsha === date.varsha && (
                    <span> • Current Masa: {date.masa_name} ({date.masa} of 16)</span>
                  )}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Gregorian Year View */}
        <Card className="shadow-xl bg-white/80 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center justify-between text-xl">
              <span>Gregorian Year (12 Months)</span>
              <div className="flex items-center space-x-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigateGregorianYear(-1)}
                  className="text-white hover:bg-white/20"
                  data-testid="gregorian-prev-year"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigateGregorianYear(1)}
                  className="text-white hover:bg-white/20"
                  data-testid="gregorian-next-year"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-indigo-900 mb-2">{gregorianDate.getFullYear()}</h3>
              <p className="text-indigo-700">365 days (366 in leap year)</p>
            </div>
            <div className="grid grid-cols-4 gap-4 mb-4">
              {gregorianMonths.map((monthName, index) => {
                const monthNumber = index + 1;
                const today = new Date();
                const isCurrentMonth = monthNumber === (today.getMonth() + 1) && 
                  gregorianDate.getFullYear() === today.getFullYear();
                return (
                  <Card 
                    key={monthNumber} 
                    className={`p-4 text-center transition-all cursor-pointer ${
                      isCurrentMonth ? 'ring-2 ring-indigo-500 bg-indigo-50 scale-105' : 'hover:shadow-md hover:scale-102'
                    }`}
                    onClick={() => handleGregorianMonthClick(index, gregorianDate.getFullYear())}
                    data-testid={`gregorian-month-${monthNumber}`}
                  >
                    <div className="text-xl font-bold text-indigo-800 mb-2">{monthNumber}</div>
                    <div className="text-sm font-medium text-indigo-600 mb-1">{monthName}</div>
                    <div className="text-xs text-gray-600">
                      {new Date(gregorianDate.getFullYear(), index + 1, 0).getDate()} days
                    </div>
                    {isCurrentMonth && (
                      <div className="text-xs font-semibold text-indigo-700 mt-2 bg-indigo-200 rounded px-1">Current</div>
                    )}
                  </Card>
                );
              })}
            </div>
            <div className="text-center text-sm text-gray-600">
              Viewing: <strong>{gregorianDate.getFullYear()}</strong>
              {gregorianDate.getFullYear() === new Date().getFullYear() && (
                <span> • Current Month: {gregorianMonths[new Date().getMonth()]} ({new Date().getMonth() + 1} of 12)</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderTithiView = () => {
    return (
      <div className="space-y-8" data-testid="tithi-view">
        {/* Location Selection */}
        <Card className="shadow-xl bg-white/80 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center space-x-2">
              <MapPin className="h-6 w-6" />
              <span>पारम्परिक तिथि गणना - Traditional Tithi Calculation</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              
              {/* Method Selection */}
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-3 block">Traditional Methods</Label>
                <div className="space-y-2 mb-3">
                  <Button
                    variant={calculationMethod === "surya_siddhanta" ? "default" : "outline"}
                    onClick={() => {
                      setCalculationMethod("surya_siddhanta");
                      fetchTraditionalTithi(selectedCity, "surya_siddhanta", kulaType);
                    }}
                    className="w-full"
                  >
                    सूर्य सिद्धान्त (Surya Siddhanta)
                  </Button>
                  <Button
                    variant={calculationMethod === "visuddha_siddhanta" ? "default" : "outline"}
                    onClick={() => {
                      setCalculationMethod("visuddha_siddhanta");
                      fetchTraditionalTithi(selectedCity, "visuddha_siddhanta", kulaType);
                    }}
                    className="w-full"
                  >
                    विशुद्ध सिद्धान्त (Visuddha Siddhanta)
                  </Button>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={locationSearchQuery}
                    onChange={(e) => {
                      setLocationSearchQuery(e.target.value);
                      searchLocations(e.target.value);
                    }}
                    onFocus={() => {
                      if (locationSearchResults.length > 0) {
                        setShowLocationResults(true);
                      }
                    }}
                    placeholder="Search for a city worldwide..."
                    className="w-full p-3 border rounded-lg text-sm bg-white pr-10"
                  />
                  {isSearchingLocation && (
                    <div className="absolute right-3 top-3">
                      <div className="animate-spin h-5 w-5 border-2 border-purple-500 border-t-transparent rounded-full"></div>
                    </div>
                  )}
                  
                  {showLocationResults && locationSearchResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {locationSearchResults.map((city, index) => (
                        <div
                          key={index}
                          onClick={() => selectLocation(city)}
                          className="p-3 hover:bg-purple-50 cursor-pointer border-b border-gray-100"
                        >
                          <div className="font-medium text-gray-800">{city.name}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            📍 {city.lat.toFixed(4)}, {city.lon.toFixed(4)} • ⏰ {city.timezone}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {selectedCityData && (
                  <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="text-sm font-medium text-purple-800">📍 Current Location</div>
                    <div className="text-sm text-purple-700">{selectedCityData.name}</div>
                    <div className="text-xs text-purple-600 mt-1">
                      Coordinates: {selectedCityData.lat.toFixed(6)}, {selectedCityData.lon.toFixed(6)}<br />
                      Timezone: {selectedCityData.timezone}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Custom Coordinates */}
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-3 block">Custom Location</Label>
                <div className="space-y-2">
                  <Input
                    placeholder="Location Name"
                    value={customLocation.name}
                    onChange={(e) => setCustomLocation({...customLocation, name: e.target.value})}
                  />
                  <Input
                    placeholder="Latitude"
                    type="number"
                    step="0.0001"
                    value={customLocation.lat || ""}
                    onChange={(e) => setCustomLocation({...customLocation, lat: parseFloat(e.target.value)})}
                  />
                  <Input
                    placeholder="Longitude"
                    type="number"
                    step="0.0001"
                    value={customLocation.lon || ""}
                    onChange={(e) => setCustomLocation({...customLocation, lon: parseFloat(e.target.value)})}
                  />
                </div>
                <Button 
                  onClick={() => fetchTithiData(null, customLocation.lat, customLocation.lon)}
                  disabled={!customLocation.lat || !customLocation.lon}
                  className="w-full mt-3 bg-indigo-600 hover:bg-indigo-700"
                >
                  Calculate Custom Location
                </Button>
              </div>
              
              {/* Method Comparison */}
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-3 block">Method Comparison</Label>
                <Button 
                  onClick={async () => {
                    const comparison = await compareTraditionalMethods(selectedCity);
                    if (comparison) {
                      toast.success("Methods compared successfully!");
                      console.log("Comparison result:", comparison);
                    }
                  }}
                  className="w-full mb-3 bg-amber-600 hover:bg-amber-700"
                >
                  Compare Both Methods
                </Button>
                {traditionalTithiData?.location && (
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="font-medium text-purple-800">{traditionalTithiData.location.name}</div>
                    <div className="text-sm text-purple-600">{traditionalTithiData.method}</div>
                    <div className="text-sm text-gray-600">
                      {traditionalTithiData.location.latitude.toFixed(4)}, {traditionalTithiData.location.longitude.toFixed(4)}
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      {new Date(traditionalTithiData.calculation_timestamp).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {traditionalTithiData && (
          <>
            {/* Current Traditional Tithi Details */}
            <Card className="shadow-xl bg-white/80 backdrop-blur-sm">
              <CardHeader className="bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center space-x-2">
                  <Star className="h-6 w-6" />
                  <span>वर्तमान तिथि - {traditionalTithiData.tithi_name} ({traditionalTithiData.method})</span>
                </CardTitle>
              </CardHeader>
                <CardContent className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    
                    <div className="text-center">
                      <div className="text-4xl font-bold text-amber-800 mb-2">
                        {traditionalTithiData.adjusted_tithi}
                      </div>
                      <div className="text-lg font-medium text-amber-600 mb-1">
                        {traditionalTithiData.tithi_name}
                      </div>
                      <div className="text-sm text-gray-600">{traditionalTithiData.paksha}</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-800 mb-2">
                        {(traditionalTithiData.tithi_progress * 100).toFixed(1)}%
                      </div>
                      <div className="text-sm font-medium text-purple-600 mb-1">Progress</div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className="bg-purple-600 h-3 rounded-full transition-all duration-300"
                          style={{ width: `${(traditionalTithiData.tithi_progress * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-800 mb-2">
                        {new Date(traditionalTithiData.tithi_end_time).toLocaleTimeString()}
                      </div>
                      <div className="text-sm font-medium text-blue-600 mb-1">End Time</div>
                      <div className="text-xs text-gray-500">
                        {new Date(traditionalTithiData.tithi_end_time).toLocaleDateString()}
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-3xl font-bold text-emerald-800 mb-2">
                        {traditionalTithiData.elongation?.toFixed(1)}°
                      </div>
                      <div className="text-sm font-medium text-emerald-600 mb-1">Elongation</div>
                      <div className="text-xs text-gray-500">
                        Moon-Sun difference
                      </div>
                    </div>
                  </div>

                  {/* Astronomical Details */}
                  <Separator className="my-6" />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="text-center">
                      <div className="font-semibold text-gray-700">सूर्य देशान्तर (Solar Longitude)</div>
                      <div className="text-gray-600">{traditionalTithiData.sun_longitude?.toFixed(2)}°</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-gray-700">चन्द्र देशान्तर (Lunar Longitude)</div>
                      <div className="text-gray-600">{traditionalTithiData.moon_longitude?.toFixed(2)}°</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-gray-700">Elongation Difference</div>
                      <div className="text-gray-600">{traditionalTithiData.elongation?.toFixed(2)}°</div>
                    </div>
                  </div>
                  
                  {traditionalTithiData.sun_equation && traditionalTithiData.moon_equation && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="text-center">
                        <div className="font-semibold text-gray-700">Sun Equation of Center</div>
                        <div className="text-gray-600">{traditionalTithiData.sun_equation.toFixed(3)}°</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-gray-700">Moon Equation of Center</div>
                        <div className="text-gray-600">{traditionalTithiData.moon_equation.toFixed(3)}°</div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Traditional Astronomical Information */}
            <Card className="shadow-xl bg-white/80 backdrop-blur-sm">
              <CardHeader className="bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center space-x-2">
                  <Sunrise className="h-6 w-6" />
                  <span>पारम्परिक ज्योतिष गणना (Traditional Astronomical Details)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-800 mb-2">
                      {traditionalTithiData.julian_day?.toFixed(2)}
                    </div>
                    <div className="text-sm font-medium text-orange-600">Julian Day</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-800 mb-2">
                      {traditionalTithiData.days_since_epoch?.toFixed(0)}
                    </div>
                    <div className="text-sm font-medium text-red-600">Days Since Kali Epoch</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-800 mb-2">
                      {traditionalTithiData.method === "Visuddha Siddhanta" ? "विशुद्ध" : "सूर्य"}
                    </div>
                    <div className="text-sm font-medium text-yellow-600">Calculation Method</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Method Information */}
            <Card className="shadow-xl bg-white/80 backdrop-blur-sm">
              <CardHeader className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center space-x-2">
                  <Calendar className="h-6 w-6" />
                  <span>सिद्धान्त विवरण (Siddhanta Details)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="p-4 bg-teal-50 rounded-lg">
                    <h4 className="font-semibold text-teal-800 mb-2">
                      {traditionalTithiData.method} Method Details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <strong>Tithi Name:</strong> {traditionalTithiData.tithi_name}
                      </div>
                      <div>
                        <strong>Paksha:</strong> {traditionalTithiData.paksha}
                      </div>
                      <div>
                        <strong>Adjusted Tithi:</strong> {traditionalTithiData.adjusted_tithi} of 15
                      </div>
                      <div>
                        <strong>Progress:</strong> {(traditionalTithiData.tithi_progress * 100).toFixed(2)}%
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">
                      {traditionalTithiData.method === "Surya Siddhanta" ? 
                        "सूर्य सिद्धान्त - Ancient astronomical treatise used for precise celestial calculations" :
                        "विशुद्ध सिद्धान्त - Refined astronomical methods with enhanced precision"
                      }
                    </p>
                    <div className="text-xs text-gray-500">
                      Custom Tithi Names: अं काली, आं कपालिनी, इं कुल्ला, ईं कुरुकुल्ला, उं विरोधिनी...
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  };

  // Dual calendar views implemented above

  const renderEventsView = () => {
    return (
      <div className="space-y-6" data-testid="events-view">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-2xl font-bold text-amber-900">Divine Events Planning</h3>
            <p className="text-amber-700">Manage events in the Devimana timeline</p>
          </div>
          <EventPlanner currentDate={currentDate} onEventCreated={fetchEvents} />
        </div>
        
        {events.length === 0 ? (
          <div className="text-center py-12" data-testid="no-events">
            <Calendar className="h-16 w-16 text-amber-400 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-amber-800 mb-2">No Divine Events Planned</h4>
            <p className="text-amber-700 mb-4">Start planning events in your divine timeline</p>
            <EventPlanner currentDate={currentDate} onEventCreated={fetchEvents} />
          </div>
        ) : (
          <div className="grid gap-4" data-testid="events-list">
            {events.map(event => (
              <Card key={event.id} className="shadow-lg hover:shadow-xl transition-shadow" data-testid={`event-card-${event.id}`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-800 mb-2">{event.title}</h4>
                      <p className="text-gray-600 mb-3">{event.description}</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-amber-600">
                        <div>युग {event.yuga}</div>
                        <div>परिवृत्ति {event.parivritti}</div>
                        <div>वर्ष {event.varsha}</div>
                        <div>मास {event.masa}</div>
                        <div>दिवस {event.divasa}</div>
                        <div>घटिका {event.ghatika}</div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <Badge variant="secondary" className="text-xs">
                        Day {event.yuga}-{event.parivritti}-{event.varsha}-{event.masa}-{event.divasa}
                      </Badge>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => deleteEvent(event.id)}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`delete-event-${event.id}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Gregorian Integration Info */}
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-blue-800">Gregorian Calendar Integration</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-blue-700 text-sm mb-2">
              Convert between Gregorian dates and Devimana calendar for easier planning:
            </p>
            <div className="text-xs text-blue-600">
              • Today in Gregorian: {new Date().toLocaleDateString('en-IN', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
            <div className="text-xs text-blue-600">
              • Today in Devimana: कलियुग दिवस {kali_yuga_day} • युग {date.yuga} • वर्ष {date.varsha} • मास {date.masa} • दिवस {date.divasa}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderDateLookupView = () => {
    return (
      <div className="space-y-6" data-testid="date-lookup-view">
        
        {/* Header Card */}
        <Card className="shadow-xl bg-white/90 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center space-x-3">
              <CalendarDays className="h-6 w-6" />
              <span className="text-xl">📅 Gregorian Date Credential Lookup</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <div className="max-w-md mx-auto">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Select Any Date from Gregorian Calendar
                </h3>
                <p className="text-sm text-gray-600">
                  Get complete Devimana calendar credentials for any Gregorian date
                </p>
              </div>
              
              {/* Date Picker */}
              <div className="flex flex-col space-y-2 mb-4">
                <label className="text-sm font-medium text-gray-700 text-center">
                  Choose a Gregorian Date:
                </label>
                <input
                  type="date"
                  value={lookupDate}
                  onChange={(e) => {
                    setLookupDate(e.target.value);
                    fetchDateCredentials(e.target.value);
                  }}
                  className="w-full px-4 py-3 border-2 border-green-200 rounded-lg text-lg text-center bg-white focus:border-green-500 focus:outline-none transition-colors"
                  data-testid="date-lookup-picker"
                />
              </div>

              {/* Location Search */}
              <div className="flex flex-col space-y-2 mb-4">
                <label className="text-sm font-medium text-gray-700 text-center">
                  Search Location:
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={lookupLocationQuery}
                    onChange={(e) => {
                      setLookupLocationQuery(e.target.value);
                      if (e.target.value.length > 2) {
                        searchLookupCities(e.target.value);
                      } else {
                        setLookupLocationResults([]);
                      }
                    }}
                    onFocus={() => {
                      if (lookupLocationQuery.length > 2) {
                        searchLookupCities(lookupLocationQuery);
                      }
                    }}
                    placeholder="Type city name (e.g., Mumbai, Delhi, Kolkata...)"
                    className="w-full px-4 py-3 border-2 border-green-200 rounded-lg text-lg text-center bg-white focus:border-green-500 focus:outline-none transition-colors"
                  />
                  
                  {/* Search Results Dropdown */}
                  {lookupLocationResults.length > 0 && (
                    <div className="absolute z-50 w-full bg-white border border-green-200 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-lg">
                      {lookupLocationResults.map((city, index) => (
                        <div
                          key={index}
                          className="px-4 py-3 hover:bg-green-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          onClick={() => selectLookupLocation(city)}
                        >
                          <div className="font-medium text-gray-800">{city.name}</div>
                          <div className="text-sm text-gray-500">
                            {city.timezone} • Lat: {city.lat.toFixed(3)}, Lon: {city.lon.toFixed(3)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Selected Location Display */}
                {lookupLocationData && (
                  <div className="text-center p-2 bg-green-50 rounded border">
                    <span className="text-sm font-medium text-green-800">
                      Selected: {lookupLocationData.name} ({lookupLocationData.timezone})
                    </span>
                  </div>
                )}
              </div>

              {/* Method Selection */}
              <div className="flex flex-col space-y-2">
                <label className="text-sm font-medium text-gray-700 text-center">
                  Calculation Method:
                </label>
                <select
                  value={lookupMethod}
                  onChange={(e) => {
                    setLookupMethod(e.target.value);
                    fetchDateCredentials(lookupDate);
                  }}
                  className="w-full px-4 py-3 border-2 border-green-200 rounded-lg text-lg text-center bg-white focus:border-green-500 focus:outline-none transition-colors"
                >
                  <option value="visuddha_siddhanta">Visuddha Siddhanta</option>
                  <option value="surya_siddhanta">Surya Siddhanta</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Credentials Display */}
        {lookupCredentials && (
          <Card className="shadow-xl bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardHeader className="bg-gradient-to-r from-green-700 to-emerald-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center space-x-3">
                <span className="text-2xl">📊</span>
                <div>
                  <div className="text-xl font-bold">
                    Devimana Credentials
                  </div>
                  <div className="text-green-100 text-sm font-normal">
                    for {new Date(lookupDate).toLocaleDateString('en-US', { 
                      weekday: 'long',
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              
              {/* Main Credentials Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                
                {/* Calendar Hierarchy */}
                <Card className="bg-white/80 border-green-300">
                  <CardHeader className="bg-green-100 rounded-t-lg pb-3">
                    <CardTitle className="text-green-800 text-lg">🕐 Calendar Hierarchy</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-700">युग (Yuga):</span>
                      <span className="text-lg font-bold text-green-700">{lookupCredentials.yuga}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-700">परिवृत्ति:</span>
                      <span className="text-lg font-bold text-green-700">{lookupCredentials.parivritti}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-700">वर्ष (Varsha):</span>
                      <span className="text-lg font-bold text-green-700">{lookupCredentials.varsha}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-700">मास (Masa):</span>
                      <span className="text-lg font-bold text-green-700">{lookupCredentials.masa}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-700">दिवस (Divasa):</span>
                      <span className="text-lg font-bold text-green-700">{lookupCredentials.divasa}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Names & Identities */}
                <Card className="bg-white/80 border-blue-300">
                  <CardHeader className="bg-blue-100 rounded-t-lg pb-3">
                    <CardTitle className="text-blue-800 text-lg">🔤 Sanskrit Names</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-3">
                    <div>
                      <span className="text-sm text-gray-600">Yuga Name:</span>
                      <div className="text-lg font-semibold text-blue-700">{lookupCredentials.yuga_name}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Parivritti Name:</span>
                      <div className="text-lg font-semibold text-blue-700">{lookupCredentials.parivritti_name}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Varsha Name:</span>
                      <div className="text-lg font-semibold text-blue-700">{lookupCredentials.varsha_name}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Masa Name:</span>
                      <div className="text-lg font-semibold text-blue-700">{lookupCredentials.masa_name}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Divasa Name:</span>
                      <div className="text-lg font-semibold text-blue-700">{lookupCredentials.divasa_name}</div>
                    </div>
                  </CardContent>
                </Card>

                {/* Astronomical & Tithi */}
                <Card className="bg-white/80 border-purple-300">
                  <CardHeader className="bg-purple-100 rounded-t-lg pb-3">
                    <CardTitle className="text-purple-800 text-lg">🌙 Astronomical Data</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-700">वासर:</span>
                      <span className="text-sm font-bold text-purple-700">{lookupCredentials.vasara_name}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-700">तिथि:</span>
                      <span className="text-sm font-bold text-purple-700">{lookupCredentials.tithi_name}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-700">पक्ष:</span>
                      <span className="text-sm font-bold text-purple-700">{lookupCredentials.paksha}</span>
                    </div>
                    {lookupCredentials.dina_nitya?.letter && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-gray-700">दिन नित्या:</span>
                          <span className="text-lg font-bold text-purple-700">{lookupCredentials.dina_nitya.letter}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-gray-700">Cycle:</span>
                          <span className="text-xs font-bold text-purple-700">{lookupCredentials.dina_nitya.cycle_name}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-gray-700">Position:</span>
                          <span className="text-xs font-bold text-purple-700">{lookupCredentials.dina_nitya.position} of {lookupCredentials.dina_nitya.total_positions}</span>
                        </div>
                        {lookupCredentials.dina_nitya.tantra_type === 'paramananda' && lookupCredentials.dina_nitya.paksha && (
                          <div className="p-2 bg-purple-100 rounded text-xs text-purple-800">
                            {lookupCredentials.dina_nitya.tithi_name} {lookupCredentials.dina_nitya.paksha}
                          </div>
                        )}
                      </>
                    )}
                    <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                      <div className="text-xs text-gray-600">Kali Yuga Day:</div>
                      <div className="text-lg font-bold text-purple-800">{lookupCredentials.kali_yuga_day?.toLocaleString()}</div>
                    </div>
                  </CardContent>
                </Card>

              </div>

              {/* Daily Tithis Section */}
              {lookupDailyTithis && (
                <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 border-indigo-200 mt-6">
                  <CardHeader className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-t-lg">
                    <CardTitle className="flex items-center space-x-3">
                      <span className="text-2xl">🌅</span>
                      <div>
                        <div className="text-xl font-bold">
                          Daily Tithis (Sunrise to Sunrise)
                        </div>
                        <div className="text-indigo-100 text-sm font-normal">
                          Location: {lookupDailyTithis.location?.name} | Method: {lookupDailyTithis.method}
                        </div>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    
                    {/* Sunrise Times */}
                    <div className="mb-6 p-4 bg-indigo-100 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm text-indigo-700 font-semibold">Sunrise:</div>
                          <div className="text-lg font-bold text-indigo-900">
                            {new Date(lookupDailyTithis.sunrise).toLocaleString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              timeZone: lookupLocationData?.timezone || 'Asia/Kolkata',
                              timeZoneName: 'short'
                            })}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-indigo-700 font-semibold">Next Sunrise:</div>
                          <div className="text-lg font-bold text-indigo-900">
                            {new Date(lookupDailyTithis.next_sunrise).toLocaleString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              timeZone: lookupLocationData?.timezone || 'Asia/Kolkata',
                              timeZoneName: 'short'
                            })}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tithis List */}
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-indigo-800 mb-3">
                        Tithis for this Solar Day:
                      </h4>
                      {lookupDailyTithis.tithis && lookupDailyTithis.tithis.length > 0 ? (
                        lookupDailyTithis.tithis.map((tithi, index) => (
                          <Card key={index} className="bg-white/80 border-indigo-300">
                            <CardContent className="p-4">
                              <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-2 md:space-y-0">
                                <div className="flex-1">
                                  <div className="text-xl font-bold text-indigo-800">{tithi.tithi_name}</div>
                                  <div className="text-sm text-indigo-600">{tithi.paksha}</div>
                                </div>
                                <div className="flex-1 text-right md:text-center">
                                  <div className="text-sm text-gray-600 font-semibold">Start Time:</div>
                                  <div className="text-sm font-mono text-gray-800">
                                    {new Date(tithi.start).toLocaleString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      timeZone: lookupLocationData?.timezone || 'Asia/Kolkata',
                                      timeZoneName: 'short'
                                    })}
                                  </div>
                                </div>
                                <div className="flex-1 text-right">
                                  <div className="text-sm text-gray-600 font-semibold">End Time:</div>
                                  <div className="text-sm font-mono text-gray-800">
                                    {new Date(tithi.end).toLocaleString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      timeZone: lookupLocationData?.timezone || 'Asia/Kolkata',
                                      timeZoneName: 'short'
                                    })}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      ) : (
                        <div className="text-center text-gray-500 p-4">
                          No tithi information available for this day.
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Instructions */}
              <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">💡</span>
                    <div className="text-sm text-amber-800">
                      <strong>How to use:</strong> Select any date from the Gregorian calendar above to instantly view 
                      its corresponding Devimana calendar credentials including Yuga, Varsha, Masa, Divasa, Vasara names, 
                      and astronomical information.
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Generate Saṅkalpa Section */}
              <Card className="bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200 mt-6">
                <CardContent className="p-6 text-center">
                  <h4 className="text-lg font-semibold text-emerald-800 mb-3">Generate Saṅkalpa for Selected Date</h4>
                  <p className="text-sm text-emerald-700 mb-4">
                    Create a traditional Saṅkalpa declaration using the Devimana calendar credentials for {new Date(lookupDate).toLocaleDateString('en-US', { 
                      weekday: 'long',
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                  <Button
                    onClick={async () => {
                      if (isAuthenticated) {
                        await generateSankalpaForDate(lookupDate, lookupCredentials);
                        setShowDateSankalpa(true);
                      } else {
                        setShowLoginModal(true);
                      }
                    }}
                    className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white px-6 py-2"
                    disabled={!lookupCredentials || sankalpaPending}
                  >
                    <Star className="h-4 w-4 mr-2" />
                    {sankalpaPending ? 'Generating...' : 'Generate Saṅkalpa for This Date'}
                    {!isAuthenticated && <span className="ml-1 text-xs">(Login Required)</span>}
                  </Button>
                </CardContent>
              </Card>

            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {!lookupCredentials && (
          <Card className="shadow-lg bg-gray-50 border-gray-200">
            <CardContent className="p-8 text-center">
              <div className="text-gray-600">
                <CalendarDays className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg">Select a date to view Devimana credentials</p>
                <p className="text-sm mt-2">Choose any Gregorian date from the picker above</p>
              </div>
            </CardContent>
          </Card>
        )}

      </div>
    );
  };
  return (
    <>
    <div className="app-container">
      {/* Mobile App Header */}
      <div className="app-header">
        {/* Status Indicators */}
        <div className="status-indicator">
          {!isOnline && (
            <span className="status-badge offline">Offline</span>
          )}
          {deviceInfo && (
            <span className="status-badge">{deviceInfo.platform}</span>
          )}
        </div>
        
        {/* Menu Button */}
        <button 
          className="menu-button"
          onClick={() => setShowSidebar(true)}
          data-testid="menu-button"
        >
          <Menu className="h-5 w-5" />
        </button>
        
        <h1>{t.devimanaCalendar}</h1>
        <p>कलियुग दिवस: {kali_yuga_day.toLocaleString()}</p>
        
        {/* Top Navigation Bar - Only for Mobile App */}
        {Capacitor.isNativePlatform() && (
          <div className="top-nav-container mobile-app-only">
            <div className="top-nav-scroll">
              <button 
                className={`top-nav-item ${activeView === "dashboard" ? "active" : ""}`}
                onClick={() => setActiveView("dashboard")}
                data-testid="dashboard-tab"
              >
                <Calendar className="top-nav-icon" />
                <span>Home</span>
              </button>
              
              <button 
                className={`top-nav-item ${activeView === "week" ? "active" : ""}`}
                onClick={() => setActiveView("week")}
                data-testid="week-tab"
              >
                <CalendarDays className="top-nav-icon" />
                <span>Week</span>
              </button>
              
              <button 
                className={`top-nav-item ${activeView === "year" ? "active" : ""}`}
                onClick={() => setActiveView("year")}
                data-testid="year-tab"
              >
                <Grid className="top-nav-icon" />
                <span>Year</span>
              </button>
              
              <button 
                className={`top-nav-item ${activeView === "tithi" ? "active" : ""}`}
                onClick={() => setActiveView("tithi")}
                data-testid="tithi-tab"
              >
                <Moon className="top-nav-icon" />
                <span>Tithi</span>
              </button>
              
              {isAuthenticated && (
                <button 
                  className={`top-nav-item ${activeView === "events" ? "active" : ""}`}
                  onClick={() => setActiveView("events")}
                  data-testid="events-tab"
                >
                  <Star className="top-nav-icon" />
                  <span>Events</span>
                </button>
              )}
              
              {!isAuthenticated && (
                <button 
                  className={`top-nav-item ${activeView === "datelookup" ? "active" : ""}`}
                  onClick={() => setActiveView("datelookup")}
                  data-testid="datelookup-tab"
                >
                  <Eye className="top-nav-icon" />
                  <span>Lookup</span>
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Breadcrumb Navigation for selected dates */}
        {(selectedDevimanaDate || selectedDate) && (
          <div className="mt-2 text-center">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={goToToday}
              className="text-amber-700 hover:bg-amber-100 text-xs"
            >
              ← Today
            </Button>
          </div>
        )}
      </div>

      {/* Dynamic Sidebar */}
      {showSidebar && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowSidebar(false)}
          ></div>
          
          {/* Sidebar */}
          <div className="fixed top-0 right-0 h-full w-80 max-w-[90vw] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out">
            {/* Sidebar Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50">
              <h3 className="text-lg font-semibold text-amber-900">Menu</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSidebar(false)}
                className="text-amber-600 hover:text-amber-800"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Sidebar Content */}
            <div className="p-4 space-y-4">
              {/* Navigation Menu - Only for Web Version */}
              {!Capacitor.isNativePlatform() && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-amber-900 mb-3">Navigation</h4>
                  
                  <button 
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                      activeView === "dashboard" 
                        ? "bg-amber-100 text-amber-900 border border-amber-200" 
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={() => {
                      setActiveView("dashboard");
                      setShowSidebar(false);
                    }}
                  >
                    <Calendar className="h-5 w-5" />
                    <span className="font-medium">Dashboard</span>
                  </button>
                  
                  <button 
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                      activeView === "week" 
                        ? "bg-amber-100 text-amber-900 border border-amber-200" 
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={() => {
                      setActiveView("week");
                      setShowSidebar(false);
                    }}
                  >
                    <CalendarDays className="h-5 w-5" />
                    <span className="font-medium">Week Views</span>
                  </button>
                  
                  <button 
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                      activeView === "year" 
                        ? "bg-amber-100 text-amber-900 border border-amber-200" 
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={() => {
                      setActiveView("year");
                      setShowSidebar(false);
                    }}
                  >
                    <Grid className="h-5 w-5" />
                    <span className="font-medium">Year Views</span>
                  </button>
                  
                  <button 
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                      activeView === "tithi" 
                        ? "bg-amber-100 text-amber-900 border border-amber-200" 
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={() => {
                      setActiveView("tithi");
                      setShowSidebar(false);
                    }}
                  >
                    <Moon className="h-5 w-5" />
                    <span className="font-medium">Traditional Tithi</span>
                  </button>
                  
                  {isAuthenticated && (
                    <button 
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                        activeView === "events" 
                          ? "bg-amber-100 text-amber-900 border border-amber-200" 
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                      onClick={() => {
                        setActiveView("events");
                        setShowSidebar(false);
                      }}
                    >
                      <Star className="h-5 w-5" />
                      <span className="font-medium">Events</span>
                    </button>
                  )}
                  
                  <button 
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                      activeView === "datelookup" 
                        ? "bg-amber-100 text-amber-900 border border-amber-200" 
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={() => {
                      setActiveView("datelookup");
                      setShowSidebar(false);
                    }}
                  >
                    <Eye className="h-5 w-5" />
                    <span className="font-medium">Date Lookup</span>
                  </button>
                </div>
              )}

              {/* User Status */}
              <div className={!Capacitor.isNativePlatform() ? "border-t pt-4" : ""}>
                {isAuthenticated ? (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center space-x-2 mb-3">
                      <User className="h-5 w-5 text-green-600" />
                      <span className="font-medium text-green-800">Welcome!</span>
                    </div>
                    <p className="text-sm text-green-700 mb-3">{user?.name}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        logout();
                        setShowSidebar(false);
                      }}
                      className="w-full text-red-600 border-red-300 hover:bg-red-50"
                    >
                      Logout
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => {
                        setShowLoginModal(true);
                        setShowSidebar(false);
                      }}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      <LogIn className="h-4 w-4 mr-2" />
                      Login
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowRegisterModal(true);
                        setShowSidebar(false);
                      }}
                      className="w-full text-amber-600 border-amber-300 hover:bg-amber-50"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Register
                    </Button>
                  </div>
                )}
              </div>

              {/* Settings Section */}
              <div className="border-t pt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowSettings(!showSettings);
                    setShowSidebar(false);
                  }}
                  className="w-full justify-start text-amber-600 hover:bg-amber-50"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </Button>
                
                {/* Install on Device */}
                {showInstallButton && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      handleInstallApp();
                      setShowSidebar(false);
                    }}
                    className="w-full justify-start text-blue-600 hover:bg-blue-50 mt-2"
                  >
                    <Smartphone className="h-4 w-4 mr-2" />
                    Install on Device
                  </Button>
                )}
                
                {/* Mobile Settings (if mobile) */}
                {isMobile && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowMobileSettings(true);
                      setShowSidebar(false);
                    }}
                    className="w-full justify-start text-amber-600 hover:bg-amber-50 mt-2"
                  >
                    <Smartphone className="h-4 w-4 mr-2" />
                    Mobile Settings
                  </Button>
                )}
                
                {/* Admin Panel Link */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    window.location.href = '/admin';
                  }}
                  className="w-full justify-start text-purple-600 hover:bg-purple-50 mt-2"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Admin Panel
                </Button>
              </div>

              {/* App Info */}
              <div className="border-t pt-4 mt-6">
                <div className="text-xs text-gray-500 space-y-1">
                  <p>Devimana Ashtanga Calendar</p>
                  <p>Complete देवीमान अष्टांग Calendar</p>
                  {deviceInfo && (
                    <p>{deviceInfo.platform} App</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <div className="app-content">
        {/* Content based on active view */}
        <div className="page-transition">
          {activeView === "dashboard" && renderDashboard()}
          {activeView === "week" && renderDualWeekView()}
          {activeView === "year" && renderDualYearView()}
          {activeView === "tithi" && renderTithiView()}
          {activeView === "events" && renderEventsView()}
          {activeView === "datelookup" && renderDateLookupView()}
        </div>
      </div>

      {/* Navigation moved to sidebar */}

      {/* Sankalpa Modal */}
      {showSankalpa && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-orange-600 text-white p-6 rounded-t-lg">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold flex items-center">
                    <Star className="h-6 w-6 mr-3" />
                    {t.sankalpaTitle}
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSankalpa(false)}
                    className="text-white hover:bg-white/20 text-xl font-bold"
                  >
                    ×
                  </Button>
                </div>
              </div>

              <div className="p-8">
                {/* Current Date Information */}
                <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="text-lg font-semibold text-amber-800 mb-2">Current Devimana Date:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-amber-700">
                    <div><strong>Yuga:</strong> {date?.yuga_name} ({date?.yuga})</div>
                    <div><strong>Parivritti:</strong> {date?.parivritti_name} ({date?.parivritti})</div>
                    <div><strong>Varsha:</strong> {date?.varsha_name} ({date?.varsha})</div>
                    <div><strong>Masa:</strong> {date?.masa_name} ({date?.masa})</div>
                    <div><strong>Divasa:</strong> {date?.divasa_name} ({date?.divasa})</div>
                    <div><strong>Vasara:</strong> {date?.vasara_name}</div>
                    <div><strong>Dina Nitya:</strong> {dinaNitya?.letter} ({dinaNitya?.cycle_name})</div>
                    <div><strong>Tithi:</strong> {traditionalTithiData?.tithi_name} ({traditionalTithiData?.paksha || 'शुक्ल पक्ष'})</div>
                  </div>
                </div>

                {/* Sankalpa Text */}
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-lg border-2 border-amber-200">
                  <h4 className="text-lg font-semibold text-amber-800 mb-4">Saṅkalpa Declaration:</h4>
                  <div 
                    className="text-lg leading-relaxed font-serif text-gray-800 select-all"
                    style={{ lineHeight: '1.8' }}
                  >
                    {currentSankalpa}
                  </div>
                </div>

                {/* Instructions */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h5 className="font-semibold text-blue-800 mb-2">Instructions:</h5>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• You can select and copy this Saṅkalpa text for your spiritual practice</li>
                    <li>• The template includes current Devimana calendar details and astronomical data</li>
                    <li>• Fill in your personal details (gotra, name) where indicated with dots</li>
                    <li>• This Saṅkalpa is generated based on your current settings (Tantra type, Tithi method)</li>
                  </ul>
                </div>

                {/* Close Button */}
                <div className="mt-6 text-center">
                  <Button
                    onClick={() => setShowSankalpa(false)}
                    className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 px-8"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Date Lookup Sankalpa Modal */}
        {showDateSankalpa && lookupCredentials && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-green-600 text-white p-6 rounded-t-lg">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold flex items-center">
                    <Star className="h-6 w-6 mr-3" />
                    Saṅkalpa for {new Date(lookupDate).toLocaleDateString('en-US', { 
                      weekday: 'long',
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDateSankalpa(false)}
                    className="text-white hover:bg-white/20 text-xl font-bold"
                  >
                    ×
                  </Button>
                </div>
              </div>

              <div className="p-8">
                {/* Selected Date Information */}
                <div className="mb-6 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <h4 className="text-lg font-semibold text-emerald-800 mb-2">Devimana Calendar for Selected Date:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-emerald-700">
                    <div><strong>Yuga:</strong> {lookupCredentials.yuga_name} ({lookupCredentials.yuga})</div>
                    <div><strong>Parivritti:</strong> {lookupCredentials.parivritti_name} ({lookupCredentials.parivritti})</div>
                    <div><strong>Varsha:</strong> {lookupCredentials.varsha_name} ({lookupCredentials.varsha})</div>
                    <div><strong>Masa:</strong> {lookupCredentials.masa_name} ({lookupCredentials.masa})</div>
                    <div><strong>Divasa:</strong> {lookupCredentials.divasa_name} ({lookupCredentials.divasa})</div>
                    <div><strong>Vasara:</strong> {lookupCredentials.vasara_name}</div>
                    <div><strong>Dina Nitya:</strong> {lookupCredentials.dina_nitya?.letter} ({lookupCredentials.dina_nitya?.cycle_name})</div>
                    <div><strong>Tithi:</strong> {lookupCredentials.tithi_name} ({lookupCredentials.paksha})</div>
                  </div>
                </div>

                {/* Sankalpa Text */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-lg border-2 border-emerald-200">
                  <h4 className="text-lg font-semibold text-emerald-800 mb-4">Saṅkalpa Declaration:</h4>
                  <div 
                    className="text-lg leading-relaxed font-serif text-gray-800 select-all"
                    style={{ lineHeight: '1.8' }}
                  >
                    {dateSankalpa}
                  </div>
                </div>

                {/* Instructions */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h5 className="font-semibold text-blue-800 mb-2">Instructions:</h5>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• This Saṅkalpa is generated for the selected date: {new Date(lookupDate).toLocaleDateString()}</li>
                    <li>• You can select and copy this text for your spiritual practice on that specific date</li>
                    <li>• Fill in your personal details (gotra, name) where indicated with dots</li>
                    <li>• The calendar values are calculated based on your current settings (Tantra type, Tithi method)</li>
                  </ul>
                </div>

                {/* Close Button */}
                <div className="mt-6 text-center">
                  <Button
                    onClick={() => setShowDateSankalpa(false)}
                    className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 px-8"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Global Settings Panel - Available on all tabs */}
        {showSettings && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">{t.settings}</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSettings(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ×
                </Button>
              </div>

              <div className="space-y-6">
                {/* Tithi Method Selection */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">{t.tithiPreferences}</h4>
                  <div className="space-y-2">
                    <div 
                      className={`p-3 border rounded cursor-pointer ${
                        tithiMethod === 'surya_siddhanta' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                      }`}
                      onClick={() => setTithiMethod('surya_siddhanta')}
                    >
                      <div className="font-medium">सूर्य सिद्धान्त</div>
                      <div className="text-xs text-gray-600">Traditional Hindu calculations</div>
                    </div>
                    <div 
                      className={`p-3 border rounded cursor-pointer ${
                        tithiMethod === 'visuddha_siddhanta' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                      }`}
                      onClick={() => setTithiMethod('visuddha_siddhanta')}
                    >
                      <div className="font-medium">विशुद्ध सिद्धान्त</div>
                      <div className="text-xs text-gray-600">Refined astronomical constants</div>
                    </div>
                  </div>
                </div>

                {/* Kula Type Selection */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">कुल परम्परा (Kula Tradition)</h4>
                  <div className="space-y-2">
                    <div 
                      className={`p-3 border rounded cursor-pointer ${
                        kulaType === 'shri_kula' ? 'border-orange-500 bg-orange-50' : 'border-gray-300'
                      }`}
                      onClick={() => setKulaType('shri_kula')}
                    >
                      <div className="font-medium">श्रीकुल</div>
                      <div className="text-xs text-gray-600">Shrikula tradition (Lakshmi-centered)</div>
                    </div>
                    <div 
                      className={`p-3 border rounded cursor-pointer ${
                        kulaType === 'kali_kula' ? 'border-orange-500 bg-orange-50' : 'border-gray-300'
                      }`}
                      onClick={() => setKulaType('kali_kula')}
                    >
                      <div className="font-medium">कालीकुल</div>
                      <div className="text-xs text-gray-600">Kalikula tradition (Kali-centered)</div>
                    </div>
                  </div>
                </div>

                {/* Tantra Type Selection */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">तन्त्र पद्धति (Tantra System)</h4>
                  <div className="space-y-2">
                    <div 
                      className={`p-3 border rounded cursor-pointer ${
                        tantraType === 'saubhagya' ? 'border-purple-500 bg-purple-50' : 'border-gray-300'
                      }`}
                      onClick={() => setTantraType('saubhagya')}
                    >
                      <div className="font-medium">सौभाग्य तन्त्र</div>
                      <div className="text-xs text-gray-600">16-letter cycle (अ to अः)</div>
                    </div>
                    <div 
                      className={`p-3 border rounded cursor-pointer ${
                        tantraType === 'paramananda' ? 'border-purple-500 bg-purple-50' : 'border-gray-300'
                      }`}
                      onClick={() => setTantraType('paramananda')}
                    >
                      <div className="font-medium">परमानन्द तन्त्र</div>
                      <div className="text-xs text-gray-600">Paksha-based pattern (शुक्ल/कृष्ण)</div>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <Button
                  onClick={async () => {
                    try {
                      // Save settings to localStorage (language is now fixed)
                      localStorage.setItem('devimana_tithi_method', tithiMethod);
                      localStorage.setItem('devimana_implementation', implementationType);
                      localStorage.setItem('devimana_kula_type', kulaType);
                      localStorage.setItem('devimana_tantra_type', tantraType);
                      
                      // Apply Kula type to backend
                      await axios.post(`${API}/devimana/kula-type?kula_type=${kulaType}`);
                      
                      toast.success('Settings saved successfully!');
                      setShowSettings(false);
                      // Force re-render by updating the date
                      fetchCurrentDate();
                      
                      // Refresh tithi data with new Kula type
                      if (selectedCity) {
                        fetchTraditionalTithi(selectedCity, tithiMethod, kulaType);
                      }
                      
                      // Refresh Dina Nitya with new Tantra type
                      fetchDinaNitya(tantraType);
                    } catch (error) {
                      console.error('Failed to save settings:', error);
                      toast.error('Failed to apply Kula settings');
                    }
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {t.saveSettings}
                </Button>
              </div>
            </div>
          </div>
        )}

      {/* Footer */}
      <div className="mt-12 text-center text-amber-700">
        <p className="text-sm">
          देवीमान अष्टांग पञ्चाङ्ग - Complete Divine Calendar with All Features
        </p>
        <p className="text-xs mt-1 text-amber-600">
          Features: Event Planning, Week View (9 Vasaras), Tithi Calculations, Gregorian Integration
        </p>
        <div className="mt-4 pt-4 border-t border-amber-200">
          <p className="text-xs text-amber-600 italic">
            Envisioned and Designed by Tapānanda-kiṅkara Mānavānanda Tīrtha
          </p>
        </div>
      </div>
      
      {/* Authentication Modals */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
      />
      <RegisterModal 
        isOpen={showRegisterModal} 
        onClose={() => setShowRegisterModal(false)} 
      />
      
      {/* Mobile Settings Modal */}
      <MobileSettings 
        isOpen={showMobileSettings} 
        onClose={() => setShowMobileSettings(false)} 
      />

      {/* Yoga Checker Modal */}
      {showYogaChecker && (
        <YogaChecker
          onClose={() => setShowYogaChecker(false)}
          selectedDate={new Date().toISOString().split('T')[0]}
          kulaType={kulaType}
        />
      )}
      
      <Toaster position="top-right" />
    </div>
    </>
  );
}

// Main App Router Component
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment (not query params) for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST before checking existing session_token
  // MUST use useLocation().hash (reactive), not window.location.hash (not reactive)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return <DevimanaCalendar />;
}

// Main App component with AuthProvider
function App() {
  return (
    <AuthProvider>
      <AppRouter />
    </AuthProvider>
  );
}

export default App;